import CreateButton from "../components/CreateButton"
import ManualApplicationButton from '../components/ManualApplicationButton'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import EasePicker, { AmpPlugin, PresetPlugin, RangePlugin } from "react-easepick"
import { useTranslation } from "react-i18next"
import { ReactSortable } from "react-sortablejs"
import TextareaAutosize from 'react-textarea-autosize'
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add-circle.svg"
import { ReactComponent as ClockIcon } from "../assets/images/icons/ic-clock.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ReactComponent as EyeIcon } from "../assets/images/icons/ic-eye.svg"
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg"
import { ReactComponent as PhoneIcon } from "../assets/images/icons/ic-phone.svg"
import { ReactComponent as SendIcon } from "../assets/images/icons/ic-send.svg"
import { ReactComponent as WhatsappIcon } from "../assets/images/icons/ic-whatsapp.svg"
import { ReactComponent as TestIcon } from "../assets/images/icons/ic-corrections.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { ButtonStatus, CourseType, MailStatus } from "../common/constants"
import { Each } from "../common/Each"
// removed unused useContextMenu
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import MainContext from "../common/MainContext"
import { canAccessPage } from "../common/permissions"
import Badge from "../components/Badge"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import Checkbox from "../components/Checkbox"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import MultipleSelect from "../components/MultipleSelect"
import Pagination from "../components/Pagination"
import Select from "../components/Select"
import StatusBadge from "../components/StatusBadge"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Toggle from "../components/Toggle"
import TooltipWrapper from '../components/TooltipWrapper'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts"
import { DialogStatus } from "../enums"
import typo from "../typography.module.css"
import { formatDateV2, formatNumber, formatTimeV2, getLogPhrase, getMailStatusBadge, getOrderStatusBadge, groupLogsByDate, UTCtoLocal } from "../utils"
import styles from "./Applications.module.css"
import { Helmet, HelmetProvider } from "react-helmet-async"
import useContextMenu from "../common/hooks/useContextMenu"

const apiGetNoTimeout = (url, config = {}) => api.get(url, { ...config, timeout: 0 })
const apiPostNoTimeout = (url, data, config = {}) => api.post(url, data, { ...config, timeout: 0 })
const apiPutNoTimeout = (url, data, config = {}) => api.put(url, data, { ...config, timeout: 0 })
const apiDeleteNoTimeout = (url, config = {}) => api.delete(url, { ...config, timeout: 0 })

const isManagedBySalesV2 = (application) => Boolean(
    application?.managed_by_sales_v2 || application?.sales_opportunity_id
)

const calculatePercentageDifference = (actualTotal, previousTotal) => {
    const actual = Number(actualTotal ?? 0)
    const previous = Number(previousTotal ?? 0)

    if (previous === 0) {
        return actual > 0 ? Infinity : actual < 0 ? -Infinity : 0
    }

    return ((actual - previous) / Math.abs(previous)) * 100
}

const getStoredLeadUtm = (rawMetadata) => {
    let metadata = rawMetadata

    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata)
        }
        catch {
            metadata = null
        }
    }

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return []
    }

    return [
        { key: 'utm_source', label: 'Source' },
        { key: 'utm_medium', label: 'Medium' },
        { key: 'utm_campaign', label: 'Campaign' },
        { key: 'utm_content', label: 'Content' },
    ]
        .map(({ key, label }) => ({
            key,
            label,
            value: key === 'utm_source'
                ? (String(metadata.utm_source ?? '').trim() || metadata.source)
                : metadata[key]
        }))
        .filter(({ value }) => value !== null && value !== undefined && String(value).trim() !== '')
}

const Applications = () => {

    const ONLY_ASSIGNED_FILTER_STORAGE_KEY = 'applications.onlyAssignedToMe'

    const context = useContext(MainContext)
    const { width } = useWindowDimensions()
    const { t } = useTranslation()
    const [openDialog, setOpenDialog] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [applications, setApplications] = useState([])
    const [application, setApplication] = useState({ name: '', questions: [], status: 'draft' })
    const [filteredApplications, setFilteredApplications] = useState([])
    const [usersApplications, setUsersApplications] = useState([])
    const [usersApplicationsCount, setUsersApplicationsCount] = useState(0)
    const [overviewLeadTables, setOverviewLeadTables] = useState({
        recent: [],
        contactPriority: [],
    })
    const [loadingOverviewLeads, setLoadingOverviewLeads] = useState(false)
    const [loadingSalesTeamPerformance, setLoadingSalesTeamPerformance] = useState(false)
    const [salesTeamPerformanceRows, setSalesTeamPerformanceRows] = useState([])
    const [salesPerformanceRange, setSalesPerformanceRange] = useState(() => {
        const now = new Date()
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
        const start = new Date(end)
        start.setDate(start.getDate() - 6)
        start.setHours(0, 0, 0, 0)

        return {
            startDate: start.toISOString(),
            endDate: end.toISOString(),
        }
    })
    const [loadingLeadScoreHistory, setLoadingLeadScoreHistory] = useState(false)
    const [leadScoreHistory, setLeadScoreHistory] = useState([])
    const [loadingPage, setLoadingPage] = useState(false)
    const [exportingSearch, setExportingSearch] = useState(false)
    const [courses, setCourses] = useState([])
    const [utmSourceOptions, setUtmSourceOptions] = useState([])
    const [loadingUtmSources, setLoadingUtmSources] = useState(false)
    const [disableSave, setDisableSave] = useState(false)
    const [saveLoading, setSaveLoading] = useState(false)
    // removed unused 'tab' state
    const [answersDialog, setAnswersDialog] = useState(null)
    const [linkCopied, setLinkCopied] = useState(false)
    const [updatedApplication, setUpdatedApplication] = useState(null)
    const [updatedAssignee, setUpdatedAssignee] = useState(null)
    const [updatedLeadApplication, setUpdatedLeadApplication] = useState(null)
    const [leadApplicationSelectionId, setLeadApplicationSelectionId] = useState(null)
    const [selectedLog, setSelectedLog] = useState(null)
    // removed unused showMenu from context menu hook
    const [sendingMail, setSendingMail] = useState(null)
    const [cancellingMail, setCancellingMail] = useState(null)
    const [reschedulingMail, setReschedulingMail] = useState(null)
    const [viewMailContent, setViewMailContent] = useState(null)
    const [contactQrDialog, setContactQrDialog] = useState(null)
    const [loadingApplications, setLoadingApplications] = useState(false)
    const [deleteUserApplicationId, setDeleteUserApplicationId] = useState(null)
    const [touchDialogOpen, setTouchDialogOpen] = useState(false)
    const [editingTouchIndex, setEditingTouchIndex] = useState(null)
    const [savingTouch, setSavingTouch] = useState(false)
    const [deletingTouchIndex, setDeletingTouchIndex] = useState(null)
    const [deletingTouch, setDeletingTouch] = useState(false)
    const [admins, setAdmins] = useState([])
    const [touchDraft, setTouchDraft] = useState({
        type: 'whatsapp',
        contact_outcome: 'not_answered',
        lead_feedback: '',
        notes: '',
        contacted_at: new Date(),
        admin_id: null,
        admin_name: ''
    })

    const { showMenu } = useContextMenu()

    const [page, setPage] = useState(0)
    const [per, setPer] = useState(25)
    const [filters, _setFilters] = useState(() => {
        let onlyAssignedToMe = true
        try {
            const stored = localStorage.getItem(ONLY_ASSIGNED_FILTER_STORAGE_KEY)
            if (stored !== null) {
                onlyAssignedToMe = stored === 'true'
            }
        }
        catch (e) {
            console.error(e)
        }

        return {
            startDate: null,
            endDate: null,
            q: null,
            courses: [],
            status: [],
            utmSources: [],
            test: false,
            onlyAssignedToMe,
            assignedAdminId: null,
        }
    })
    const filterRef = useRef(filters)
    const setFilters = (newFilters) => {
        filterRef.current = {
            ...filterRef.current,
            ...newFilters
        }
        _setFilters(filterRef.current)
    }
    const [filterOpen, setFilterOpen] = useState(false)
    const hasActiveSearchFilters = useMemo(() => (
        Boolean(filters.startDate) ||
        Boolean(filters.endDate) ||
        Boolean(filters.q?.trim()) ||
        filters.courses.length > 0 ||
        filters.status.length > 0 ||
        filters.utmSources.length > 0 ||
        filters.assignedAdminId !== null ||
        filters.test
    ), [filters])
    const tableRef = useRef(null)
    const usersApplicationsRequestControllerRef = useRef(null)
    const usersApplicationsRequestIdRef = useRef(null)
    const excludedLeadStatuses = useMemo(() => new Set(['paid', 'ghost']), [])
    const canViewSalesPerformance = useMemo(() => {
        return canAccessPage(context.user, 'applications/sales-performance', 'view', [])
    }, [context.user])

    const options = useMemo(
        () => ({
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                '/css/easepick-range.css',
            ],
            zIndex: 1000,
            plugins: [
                RangePlugin,
                PresetPlugin,
                AmpPlugin
            ],
            lang: 'it-IT',
            format: "DD MMM YYYY",
            AmpPlugin: {
                resetButton: () => {
                    setFilters({
                        ...filterRef.current,
                        startDate: null,
                        endDate: null
                    })
                    return true
                },
                darkMode: false,
                dropdown: {
                    months: true,
                    years: true,
                    minYear: 2020
                }
            },
            PresetPlugin: {
                customPreset: {
                    'Oggi': [
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '7 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 6); // 6 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Settimana': [
                        (() => {
                            const now = new Date();
                            const day = now.getDay(); // 0 (domenica) - 6 (sabato)
                            const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
                            const monday = new Date(now.setDate(diff));
                            return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '30 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 29); // 29 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Mese': [
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // Primo giorno del mese
                        })(),
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999); // Ultimo giorno del mese
                        })(),
                    ],
                    'Anno': [
                        new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
                        new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
                    ]
                }
            },
            setup(picker) {
                picker.on("select", (e) => {
                    const { start, end } = e.detail;
                    end.setHours(23, 59, 59, 999)
                    setFilters({
                        ...filterRef.current,
                        startDate: start.toISOString(),
                        endDate: end.toISOString()
                    })
                });
            },
        }),
        []
    );

    const salesPerformanceDateOptions = useMemo(
        () => ({
            startDate: salesPerformanceRange.startDate ? new Date(salesPerformanceRange.startDate) : null,
            endDate: salesPerformanceRange.endDate ? new Date(salesPerformanceRange.endDate) : null,
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                '/css/easepick-range.css',
            ],
            zIndex: 1000,
            plugins: [
                RangePlugin,
                PresetPlugin,
                AmpPlugin
            ],
            lang: 'it-IT',
            format: "DD MMM YYYY",
            AmpPlugin: {
                resetButton: () => {
                    const now = new Date()
                    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
                    const start = new Date(end)
                    start.setDate(start.getDate() - 6)
                    start.setHours(0, 0, 0, 0)

                    setSalesPerformanceRange({
                        startDate: start.toISOString(),
                        endDate: end.toISOString(),
                    })

                    return true
                },
                darkMode: false,
                dropdown: {
                    months: true,
                    years: true,
                    minYear: 2020
                }
            },
            PresetPlugin: {
                customPreset: {
                    'Oggi': [
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '7 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 6);
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '30 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 29);
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Mese': [
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                        })(),
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                        })(),
                    ],
                }
            },
            setup(picker) {
                picker.on("select", (e) => {
                    const { start, end } = e.detail;
                    end.setHours(23, 59, 59, 999)

                    setSalesPerformanceRange({
                        startDate: start.toISOString(),
                        endDate: end.toISOString(),
                    })
                });
            },
        }),
        [salesPerformanceRange.endDate, salesPerformanceRange.startDate]
    );

    const [status, setStatus] = useState(
        [
            { id: 0, name: 'Bozza', value: 'draft' },
            { id: 1, name: 'Pubblico', value: 'public' },
            { id: 2, name: 'Nascosto', value: 'hidden' }
        ]
    )

    const userApplicationStatus = [
        {
            id: 0,
            name: 'Da contattare',
            value: 'pending'
        },
        // {
        //     id: 1,
        //     name: 'Contattato',
        //     value: 'processing'
        // },
        {
            id: 6,
            name: 'Messaggio inviato',
            value: 'message_sent'
        },
        {
            id: 7,
            name: 'Chiamata schedulata',
            value: 'call_scheduled'
        },
        {
            id: 8,
            name: 'Chiamata effettuata',
            value: 'call_completed'
        },
        {
            id: 2,
            name: 'Candidatura accettata',
            value: 'approved'
        },
        {
            id: 3,
            name: 'Candidatura rifiutata',
            value: 'rejected'
        },
        {
            id: 4,
            name: 'Vinto',
            value: 'paid'
        },
        {
            id: 5,
            name: 'Perso',
            value: 'ghost'
        },
        {
            id: 9,
            name: 'Prossima edizione',
            value: 'next_edition'
        }
    ]

    const touchTypeOptions = [
        { id: 0, name: 'WhatsApp', value: 'whatsapp' },
        { id: 1, name: 'Chiamata', value: 'call' },
        { id: 2, name: 'Email', value: 'email' }
    ]

    const touchOutcomeOptions = [
        { id: 0, name: 'Ha risposto', value: 'answered' },
        { id: 1, name: 'Non risposto', value: 'not_answered' },
        { id: 2, name: 'In attesa di risposta', value: 'awaiting_response' },
        { id: 3, name: 'Posticipato', value: 'postponed' },
        { id: 4, name: 'Contatto errato', value: 'wrong_contact' }
    ]

    const leadFeedbackOptions = [
        { id: 0, name: 'Seleziona feedback', value: '' },
        { id: 1, name: 'Propenso all\'acquisto', value: 'purchase_intent' },
        { id: 2, name: 'Richiesta d\'informazioni', value: 'info_request' },
        { id: 3, name: 'Intento errato', value: 'wrong_intent' },
        { id: 4, name: 'Non interessato', value: 'not_interested' },
        { id: 5, name: 'Obiezione sul prezzo', value: 'price_objection' },
        { id: 6, name: 'Obiezione sul prodotto', value: 'product_objection' }
    ]

    const initTouchDraft = useCallback(() => ({
        type: 'whatsapp',
        contact_outcome: 'not_answered',
        lead_feedback: '',
        notes: '',
        contacted_at: new Date(),
        admin_id: context.user?.id ?? null,
        admin_name: `${context.user?.name ?? ''} ${context.user?.surname ?? ''}`.trim()
    }), [context.user?.id, context.user?.name, context.user?.surname])

    const getTouchTypeLabel = (type) => {
        return touchTypeOptions.find((option) => option.value === type)?.name ?? type ?? '---'
    }

    const getTouchOutcomeLabel = (outcome) => {
        return touchOutcomeOptions.find((option) => option.value === outcome)?.name ?? outcome ?? '---'
    }

    const getTouchDialogOutcomeOptions = (type) => {
        const shouldShowAwaitingResponse = ['whatsapp', 'email'].includes(type)
        return touchOutcomeOptions.filter((option) => shouldShowAwaitingResponse || option.value !== 'awaiting_response')
    }

    const getLeadFeedbackLabel = (feedback) => {
        return leadFeedbackOptions.find((option) => option.value === feedback)?.name ?? '---'
    }

    const getUserApplicationStatusLabel = (statusValue) => {
        return userApplicationStatus.find((option) => option.value === statusValue)?.name ?? statusValue ?? '---'
    }

    const getUserApplicationStatusBadge = (statusValue) => {
        switch (statusValue) {
            case 'pending':
                return { name: 'Da contattare', color: 'rgba(84, 135, 255, 100%)' }
            case 'message_sent':
                return { name: 'Messaggio inviato', color: 'rgba(255, 135, 48, 100%)' }
            case 'processing':
                return { name: 'Contattato', color: 'rgba(255, 135, 48, 100%)' }
            case 'call_scheduled':
                return { name: 'Chiamata schedulata', color: 'rgba(251, 217, 101, 100%)' }
            case 'call_completed':
                return { name: 'Chiamata effettuata', color: 'rgba(106, 230, 201, 100%)' }
            case 'approved':
                return { name: 'Candidatura accettata', color: 'rgba(var(--sf-green-rgb), 100%)' }
            case 'rejected':
                return { name: 'Candidatura rifiutata', color: 'rgba(243, 55, 107, 100%)' }
            case 'paid':
                return { name: 'Vinto', color: 'rgba(var(--sf-green-rgb), 100%)' }
            case 'ghost':
                return { name: 'Perso', color: 'rgba(95, 102, 126, 100%)' }
            case 'next_edition':
                return { name: 'Prossima edizione', color: 'rgba(95, 102, 126, 100%)' }
            default:
                return { name: getUserApplicationStatusLabel(statusValue), color: 'rgba(var(--secondary-rgb), 100%)' }
        }
    }

    const getWhatsappPhone = (phoneRaw) => {
        if (!phoneRaw) {
            return ''
        }
        return String(phoneRaw).replace(/[^0-9]/g, '')
    }

    const getTelLink = (phoneRaw) => {
        if (!phoneRaw) {
            return ''
        }
        return `tel:${String(phoneRaw).replace(/\s+/g, '')}`
    }

    const openContactQr = (type) => {
        const phoneRaw = answersDialog?.user?.phone
        if (!phoneRaw) {
            return
        }

        if (type === 'phone') {
            const url = getTelLink(phoneRaw)
            setContactQrDialog({
                title: 'QR chiamata',
                actionLabel: 'Apri chiamata',
                url,
            })
            return
        }

        const whatsappPhone = getWhatsappPhone(phoneRaw)
        const url = `https://wa.me/${whatsappPhone}`
        setContactQrDialog({
            title: 'QR WhatsApp',
            actionLabel: 'Apri chat WhatsApp',
            url,
        })
    }

    const mailType = [
        {
            id: 0,
            name: 'Candidatura',
            value: 'application'
        },
        {
            id: 1,
            name: 'Informazioni',
            value: 'info'
        }
    ]

    const mailConfirmationDelay = [
        {
            id: 0,
            name: 'Immediata',
            value: 0
        },
        {
            id: 1,
            name: '30 minuti',
            value: 30
        },
        {
            id: 2,
            name: '1 ora',
            value: 60
        },
        {
            id: 3,
            name: '2 ore',
            value: 120
        },
        {
            id: 4,
            name: '6 ore',
            value: 360
        },
        {
            id: 5,
            name: '12 ore',
            value: 720
        },
        {
            id: 6,
            name: '24 ore',
            value: 1440
        },
        {
            id: 7,
            name: '48 ore',
            value: 2880
        },
        {
            id: 8,
            name: '72 ore',
            value: 4320
        },
        {
            id: 9,
            name: '1 settimana',
            value: 10080
        }
    ]

    const initializeStatus = (status) => {
        switch (status) {
            case 'draft':
                setStatus([
                    { id: 0, name: 'Bozza', value: 'draft' },
                    { id: 1, name: 'Pubblico', value: 'public' },
                ])
                break
            case 'public':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' }
                ])
                break
            case 'hidden':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' }
                ])
                break
            default: return
        }
    }

    const getApplications = async () => {
        setLoadingApplications(true)
        try {
            let applications = await apiGetNoTimeout('/admin/applications')
            setApplications(applications)
            setFilteredApplications(applications)
        }
        catch (e) {
            console.error(e)
        }
        setLoadingApplications(false)
    }

    // removed unused setUserApplicationTest

    const cancelUsersApplicationsRequest = useCallback(() => {
        if (usersApplicationsRequestControllerRef.current) {
            usersApplicationsRequestControllerRef.current.abort()
            usersApplicationsRequestControllerRef.current = null
        }
        usersApplicationsRequestIdRef.current = null
    }, [])

    const getUsersAppications = useCallback(async () => {
        setLoadingPage(true)
        cancelUsersApplicationsRequest()

        const controller = new AbortController()
        const requestId = Symbol('usersApplicationsRequest')
        usersApplicationsRequestControllerRef.current = controller
        usersApplicationsRequestIdRef.current = requestId

        try {
            const params = new URLSearchParams();

            params.append("page", page);
            params.append("per", per);
            params.append("include_lead_score", "false");

            if (filters.startDate) {
                params.append("start_date", filters.startDate);
            }
            if (filters.endDate) {
                params.append("end_date", filters.endDate);
            }

            if (filters.courses.length > 0) {
                params.append("courses_ids", filters.courses.join(","))
            }
            if (filters.status.length > 0) {
                params.append("status", filters.status.join(","));
            }
            if (filters.utmSources.length > 0) {
                params.append("utm_sources", filters.utmSources.join(","));
            }
            if (filters.test) {
                params.append("test", filters.test);
            }
            if (filters.q) {
                params.append("q", filters.q);
            }
            if (filters.assignedAdminId) params.append("assigned_admin_id", String(filters.assignedAdminId))
            if (filters.onlyAssignedToMe) {
                params.append("only_assigned_to_me", "true");
            }

            let uapp = await apiGetNoTimeout(`/admin/users_applications?${params.toString()}`, {
                signal: controller.signal,
            });

            if (usersApplicationsRequestIdRef.current !== requestId) {
                return
            }

            setUsersApplications(uapp.applications)
            setUsersApplicationsCount(uapp.count)
        }
        catch (e) {
            if (controller.signal.aborted || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') {
                return
            }
            console.error(e)
        }
        finally {
            if (usersApplicationsRequestIdRef.current === requestId) {
                setLoadingPage(false)
                usersApplicationsRequestControllerRef.current = null
                usersApplicationsRequestIdRef.current = null
            }
        }
    }, [filters, page, per, cancelUsersApplicationsRequest])

    const exportFilteredUsersApplications = useCallback(async () => {
        if (!hasActiveSearchFilters || exportingSearch) {
            return
        }

        setExportingSearch(true)
        try {
            const params = new URLSearchParams()

            if (filters.startDate) params.append("start_date", filters.startDate)
            if (filters.endDate) params.append("end_date", filters.endDate)
            if (filters.courses.length > 0) params.append("courses_ids", filters.courses.join(","))
            if (filters.status.length > 0) params.append("status", filters.status.join(","))
            if (filters.utmSources.length > 0) params.append("utm_sources", filters.utmSources.join(","))
            if (filters.test) params.append("test", "true")
            if (filters.q) params.append("q", filters.q)
            if (filters.assignedAdminId) params.append("assigned_admin_id", String(filters.assignedAdminId))
            if (filters.onlyAssignedToMe) params.append("only_assigned_to_me", "true")

            const blob = await apiGetNoTimeout(`/admin/users_applications/export/csv?${params.toString()}`, {
                responseType: 'blob',
            })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `lead-ricerca-${new Date().toISOString().slice(0, 10)}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        }
        catch (e) {
            console.error(e)
        }
        finally {
            setExportingSearch(false)
        }
    }, [exportingSearch, filters, hasActiveSearchFilters])

    const getOverviewLeads = useCallback(async () => {
        setLoadingOverviewLeads(true)
        try {
            const buildParams = (extra = {}) => {
                const params = new URLSearchParams()
                params.append("page", "0")
                params.append("per", "25")
                params.append("test", "false")
                params.append("include_lead_score", "false")

                if (filterRef.current.assignedAdminId) params.append("assigned_admin_id", String(filterRef.current.assignedAdminId))
                if (filterRef.current.onlyAssignedToMe) {
                    params.append("only_assigned_to_me", "true")
                }

                Object.entries(extra).forEach(([key, value]) => {
                    if (value === null || value === undefined || value === "") {
                        return
                    }
                    params.append(key, String(value))
                })

                return params
            }

            const now = new Date()
            const romeNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }))
            romeNow.setDate(romeNow.getDate() - 1)
            romeNow.setHours(18, 0, 0, 0)
            const recentStartDateIso = romeNow.toISOString()

            const [recentResponse, contactResponse, followUpResponse] = await Promise.all([
                apiGetNoTimeout(`/admin/users_applications?${buildParams({ start_date: recentStartDateIso }).toString()}`),
                apiGetNoTimeout(`/admin/users_applications?${buildParams({ next_action: 'contact_now,contact_today' }).toString()}`),
                apiGetNoTimeout(`/admin/users_applications?${buildParams({ next_action: 'follow_up_message,push_checkout,send_reminder_24h,verify_payment_intent,wait_user_reply' }).toString()}`),
            ])

            const normalize = (items = []) => items
                .filter((app) => {
                    if (excludedLeadStatuses.has(app?.status)) {
                        return false
                    }

                    const courseStatus = String(app?.application?.course?.status ?? '').toLowerCase()
                    if (courseStatus !== 'public') {
                        return false
                    }

                    if (app?.user_deleted) {
                        return false
                    }

                    // Backward-compatible fallback for placeholder user payloads.
                    const email = app?.user?.email
                    if (typeof email === 'string' && email.startsWith('utente.eliminato+') && email.endsWith('@velv.local')) {
                        return false
                    }

                    return true
                })
                .map((app) => {
                    const touches = Array.isArray(app?.touches) ? app.touches : []
                    let lastContactAt = null
                    for (const touch of touches) {
                        if (!touch || typeof touch !== 'object') {
                            continue
                        }

                        const candidateRaw = touch.contacted_at || touch.updated_at || touch.created_at
                        if (!candidateRaw) {
                            continue
                        }

                        const candidateDate = new Date(candidateRaw)
                        if (Number.isNaN(candidateDate.getTime())) {
                            continue
                        }

                        if (!lastContactAt || candidateDate > lastContactAt) {
                            lastContactAt = candidateDate
                        }
                    }

                    const statusUsesUpdatedAtAsContact = new Set(['message_sent', 'approved', 'call_completed'])
                    if (statusUsesUpdatedAtAsContact.has(String(app?.status ?? '').toLowerCase()) && app?.updated_at) {
                        const leadUpdatedAt = new Date(app.updated_at)
                        if (!Number.isNaN(leadUpdatedAt.getTime()) && (!lastContactAt || leadUpdatedAt > lastContactAt)) {
                            lastContactAt = leadUpdatedAt
                        }
                    }

                    const scoring = getLeadScoring(app)
                    const rawScore = scoring?.score ?? app?.lead_score ?? null
                    const score = (rawScore !== null && rawScore !== undefined && !Number.isNaN(Number(rawScore))) ? Number(rawScore) : null
                    const nextAction = scoring?.next_action ?? app?.lead_score_next_action ?? 'monitor'
                    const statusAgeHours = Number(scoring?.metrics?.status_age_hours ?? 0)
                    const reasons = scoring?.reasons ?? app?.lead_score_reasons ?? []
                    const updatedAtDate = app?.updated_at ? new Date(app.updated_at) : null
                    const updatedAtAgeHours = (updatedAtDate && !Number.isNaN(updatedAtDate.getTime()))
                        ? Math.max(0, (Date.now() - updatedAtDate.getTime()) / 3600000)
                        : null
                    const lastContactAgeHours = lastContactAt
                        ? Math.max(0, (Date.now() - lastContactAt.getTime()) / 3600000)
                        : null

                    return {
                        ...app,
                        _score: Number.isFinite(score) ? score : null,
                        _scoreSort: Number.isFinite(score) ? score : -1,
                        _nextAction: nextAction,
                        _statusAgeHours: Number.isFinite(statusAgeHours) ? statusAgeHours : 0,
                        _reasons: Array.isArray(reasons) ? reasons : [],
                        _updatedAtAgeHours: Number.isFinite(updatedAtAgeHours) ? updatedAtAgeHours : null,
                        _lastContactAgeHours: Number.isFinite(lastContactAgeHours) ? lastContactAgeHours : null,
                        _courseName: app?.application?.course?.name || '---',
                    }
                })

            const byScoreDesc = (a, b) => {
                if (b._scoreSort !== a._scoreSort) {
                    return b._scoreSort - a._scoreSort
                }
                return new Date(b.created_at) - new Date(a.created_at)
            }

            const sortByMixedScoreAndRecency = (items = []) => {
                if (items.length <= 1) {
                    return items
                }

                const withCreatedTs = items.map((item) => ({
                    ...item,
                    _createdTs: new Date(item.created_at).getTime() || 0,
                }))

                const createdValues = withCreatedTs.map((item) => item._createdTs)
                const minCreatedTs = Math.min(...createdValues)
                const maxCreatedTs = Math.max(...createdValues)
                const hasRange = maxCreatedTs > minCreatedTs

                const withMix = withCreatedTs.map((item) => {
                    const recencyNormalized = hasRange
                        ? ((item._createdTs - minCreatedTs) / (maxCreatedTs - minCreatedTs)) * 100
                        : 50
                    const mixedPriority = (item._scoreSort * 0.7) + (recencyNormalized * 0.3)

                    return {
                        ...item,
                        _mixedPriority: mixedPriority,
                    }
                })

                withMix.sort((a, b) => {
                    if (b._mixedPriority !== a._mixedPriority) {
                        return b._mixedPriority - a._mixedPriority
                    }
                    return b._createdTs - a._createdTs
                })

                return withMix
            }

            const recent = normalize(recentResponse?.applications ?? []).sort(byScoreDesc).slice(0, 25)

            const shouldExcludeFromContactPriority = (app) => {
                const hasRecentTouch = app._lastContactAgeHours !== null && app._lastContactAgeHours < 24
                const isRecentMessageSent = String(app?.status ?? '').toLowerCase() === 'message_sent'
                    && app._updatedAtAgeHours !== null
                    && app._updatedAtAgeHours < 24

                return hasRecentTouch || isRecentMessageSent
            }

            const contactPriorityRaw = sortByMixedScoreAndRecency(normalize(contactResponse?.applications ?? []))
                .filter((app) => ['contact_now', 'contact_today'].includes(app._nextAction))
                .filter((app) => !shouldExcludeFromContactPriority(app))
            const followUpForPriority = sortByMixedScoreAndRecency(normalize(followUpResponse?.applications ?? []))
                .filter((app) => {
                    if (shouldExcludeFromContactPriority(app)) {
                        return false
                    }
                    if (!['follow_up_message', 'push_checkout', 'send_reminder_24h', 'verify_payment_intent', 'wait_user_reply'].includes(app._nextAction)) {
                        return false
                    }
                    return app._lastContactAgeHours !== null && app._lastContactAgeHours > 24
                })

            const mergedPriority = [...contactPriorityRaw, ...followUpForPriority]
            const dedupedPriority = Object.values(
                mergedPriority.reduce((acc, app) => {
                    acc[app.id] = app
                    return acc
                }, {})
            )
            const contactPriority = sortByMixedScoreAndRecency(dedupedPriority).slice(0, 25)

            setOverviewLeadTables({
                recent,
                contactPriority,
            })
        }
        catch (e) {
            console.error(e)
            setOverviewLeadTables({
                recent: [],
                contactPriority: [],
            })
        }
        finally {
            setLoadingOverviewLeads(false)
        }
    }, [excludedLeadStatuses])

    const getSalesTeamPerformance = useCallback(async () => {
        if (!canViewSalesPerformance) {
            setSalesTeamPerformanceRows([])
            return
        }

        setLoadingSalesTeamPerformance(true)

        try {
            const params = new URLSearchParams()
            if (salesPerformanceRange.startDate) {
                params.append('start_date', salesPerformanceRange.startDate)
            }
            if (salesPerformanceRange.endDate) {
                params.append('end_date', salesPerformanceRange.endDate)
            }

            const response = await apiGetNoTimeout(`/admin/users_applications/sales/team/performance?${params.toString()}`)
            const rows = Array.isArray(response?.items) ? response.items : []
            setSalesTeamPerformanceRows(rows)
        }
        catch (e) {
            console.error(e)
            setSalesTeamPerformanceRows([])
        }
        finally {
            setLoadingSalesTeamPerformance(false)
        }
    }, [canViewSalesPerformance, salesPerformanceRange.endDate, salesPerformanceRange.startDate])

    useEffect(() => {

        const getCourses = async () => {
            try {
                let courses = await apiGetNoTimeout('/admin/courses')
                setCourses(courses)
            }
            catch (e) {
                console.error(e)
            }
        }

        const getAdmins = async () => {
            try {
                let admins = await apiGetNoTimeout('/admin/admins')
                setAdmins(admins.filter(a => a.active_id).map(a => ({
                    id: a.id,
                    name: `${a.name} ${a.surname}`,
                    value: a.id,
                    first_name: a.name,
                    surname: a.surname,
                })))
            }
            catch (e) {
                console.error(e)
            }
        }

        getApplications()
        getCourses()
        getUsersAppications()
        getAdmins()

    }, [getUsersAppications])

    useEffect(() => {
        const getUtmSources = async () => {
            setLoadingUtmSources(true)
            try {
                const sources = await apiGetNoTimeout('/admin/users_applications/utm_sources')
                setUtmSourceOptions((Array.isArray(sources) ? sources : []).map(source => ({
                    id: source,
                    name: source,
                    value: source,
                })))
            }
            catch (e) {
                console.error(e)
                setUtmSourceOptions([])
            }
            finally {
                setLoadingUtmSources(false)
            }
        }

        getUtmSources()
    }, [])

    useEffect(() => {
        getOverviewLeads()
    }, [getOverviewLeads, filters.onlyAssignedToMe, filters.assignedAdminId])

    useEffect(() => {
        getSalesTeamPerformance()
    }, [getSalesTeamPerformance])

    const saveApplication = async () => {
        setSaveLoading(true)
        if (application.id) {
            await updateApplication()
        }
        else {
            await createApplication()
        }
        setSaveLoading(false)
    }

    // Remove UI-only/computed fields and React elements before sending to API
    const sanitizeApplicationPayload = (app) => ({
        // keep only backend-expected fields
        name: app?.name ?? '',
        title: app?.title,
        subtitle: app?.subtitle,
        questions: Array.isArray(app?.questions) ? app.questions : [],
        status: app?.status ?? 'draft',
        expired_at: app?.expired_at ?? null,
        course_id: app?.course_id ?? null,
        send_confirmation_email: app?.send_confirmation_email ?? false,
        confirmation_delay: app?.confirmation_delay ?? null,
        pipeline_id: app?.pipeline_id ?? null,
        mail_type: app?.mail_type ?? null,
        owner_id: app?.owner_id ?? null
    })

    const createApplication = async () => {
        try {
            let newApp = await apiPostNoTimeout('/admin/applications', sanitizeApplicationPayload({ application }))
            setApplications([...applications, newApp])
            setOpenDialog(false)
            await getApplications()
        }
        catch (e) {
            console.error(e)
        }
    }

    const updateApplication = async () => {
        try {
            let payload = sanitizeApplicationPayload(application)
            let updated = await apiPutNoTimeout(`/admin/applications/${application.id}`, payload)
            let apps = applications.map(app => app.id === updated.id ? updated : app)
            setApplications(apps)
            setOpenDialog(false)
            await getApplications()
        }
        catch (e) {
            console.error(e)
        }
    }

    const deleteApplication = async () => {
        try {
            await apiDeleteNoTimeout(`/admin/applications/${application.id}`)
            setApplications(applications.filter(a => a.id !== application.id))
            setFilteredApplications(filteredApplications.filter(a => a.id !== application.id))
            setApplication({ name: '', questions: [], status: 'draft' })
            setOpenDialog(false)
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (application) {
            if (application.status === 'draft') {
                setDisableSave(application.name.trim() === '')
            }
            else {
                setDisableSave(application.name.trim() === '' || !application.course_id || (!application.owner_id && courses.find(c => c.id === application.course_id)?.type !== CourseType.Webinar))
            }
        }
    }, [application])

    // removed unused getStatusColor

    const getSelectStyle = (status) => {
        switch (status) {
            case 'pending':
                return {
                    backgroundColor: 'rgba(84, 135, 255, 50%)',
                    border: 'none',
                    minWidth: '220px',
                    height: '100%'
                }
            case 'message_sent':
            case 'processing':
                return {
                    backgroundColor: 'rgba(255, 135, 48, 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'call_scheduled':
                return {
                    backgroundColor: 'rgba(251, 217, 101, 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'call_completed':
                return {
                    backgroundColor: 'rgba(106, 230, 201, 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'approved':
                return {
                    backgroundColor: 'rgba(106, 230, 201, 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'rejected':
                return {
                    backgroundColor: 'rgba(243, 55, 107, 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'ghost':
                return {
                    backgroundColor: 'rgba(var(--sf-red-rgb), 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'paid':
                return {
                    backgroundColor: 'rgba(var(--sf-green-rgb), 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            case 'next_edition':
                return {
                    backgroundColor: 'rgba(95, 102, 126, 50%)',
                    border: 'none',
                    minWidth: '220px'
                }
            default: return {
                backgroundColor: 'rgba(var(--secondary-rgb), 50%)',
                border: 'none',
                minWidth: '220px'
            }
        }
    }

    // removed unused onFilterStateChange

    const updateUserApplication = async (id, statusOrPayload) => {
        try {
            const payload = typeof statusOrPayload === 'string'
                ? { status: statusOrPayload }
                : statusOrPayload

            const selectedApplication = payload.application_id
                ? applications.find((item) => item.id === payload.application_id)
                : null
            const selectedCourse = selectedApplication
                ? courses.find((course) => course.id === selectedApplication.course_id)
                : null
            const selectedApplicationPayload = selectedApplication
                ? {
                    ...selectedApplication,
                    course: selectedCourse ?? selectedApplication.course ?? null,
                }
                : null

            const selectedAdmin = payload.admin_id
                ? admins.find((a) => a.id === payload.admin_id)
                : null
            const assignedAdminPayload = payload.admin_id
                ? {
                    id: selectedAdmin?.id ?? payload.admin_id,
                    name: selectedAdmin?.first_name ?? selectedAdmin?.name ?? '',
                    surname: selectedAdmin?.surname ?? '',
                }
                : null

            const updated = await apiPutNoTimeout(`/admin/users_applications/${id}`, payload)

            setUsersApplications((prev) => prev.map((app) => {
                if (app.id !== id) {
                    return app
                }

                return {
                    ...app,
                    ...(payload.status ? { status: payload.status, admin: context.user } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'application_id') && selectedApplicationPayload
                        ? { application_id: selectedApplicationPayload.id, application: selectedApplicationPayload }
                        : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'admin_id') ? { admin: assignedAdminPayload } : {}),
                    ...(updated?.touches ? { touches: updated.touches } : {}),
                    updated_at: updated?.updated_at ?? app.updated_at,
                }
            }))

            setOverviewLeadTables((prev) => {
                const patchLead = (app) => {
                    if (app.id !== id) {
                        return app
                    }

                    return {
                        ...app,
                        ...(payload.status ? { status: payload.status, admin: context.user } : {}),
                        ...(Object.prototype.hasOwnProperty.call(payload, 'application_id') && selectedApplicationPayload
                            ? {
                                application_id: selectedApplicationPayload.id,
                                application: selectedApplicationPayload,
                                _courseName: selectedApplicationPayload?.course?.name ?? app._courseName,
                            }
                            : {}),
                        ...(Object.prototype.hasOwnProperty.call(payload, 'admin_id') ? { admin: assignedAdminPayload } : {}),
                        ...(updated?.touches ? { touches: updated.touches } : {}),
                        updated_at: updated?.updated_at ?? app.updated_at,
                    }
                }

                return {
                    recent: (prev.recent ?? []).map(patchLead),
                    contactPriority: (prev.contactPriority ?? []).map(patchLead),
                }
            })

            setAnswersDialog((prev) => {
                if (!prev || prev.id !== id) {
                    return prev
                }

                return {
                    ...prev,
                    ...(payload.status ? { status: payload.status, admin: context.user } : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'application_id') && selectedApplicationPayload
                        ? { application_id: selectedApplicationPayload.id, application: selectedApplicationPayload }
                        : {}),
                    ...(Object.prototype.hasOwnProperty.call(payload, 'admin_id') ? { admin: assignedAdminPayload } : {}),
                    ...(updated?.touches ? { touches: updated.touches } : {}),
                    updated_at: updated?.updated_at ?? prev.updated_at,
                }
            })

            if (Object.prototype.hasOwnProperty.call(payload, 'application_id')) {
                setLeadApplicationSelectionId(payload.application_id ?? null)
                getUsersAppications()
                getOverviewLeads()
            }

            return updated
        }
        catch (e) {
            console.error(e)
        }
    }

    const addTouchToUserApplication = async () => {
        if (savingTouch || !answersDialog?.id || !touchDraft.type) {
            return false
        }

        setSavingTouch(true)

        try {
            const isAnswered = touchDraft.contact_outcome === 'answered'

            const parsedDate = touchDraft.contacted_at instanceof Date
                ? touchDraft.contacted_at
                : (touchDraft.contacted_at ? new Date(touchDraft.contacted_at) : new Date())
            const payloadTouch = {
                type: touchDraft.type,
                contact_outcome: touchDraft.contact_outcome || null,
                lead_feedback: isAnswered ? (touchDraft.lead_feedback || null) : null,
                notes: touchDraft.notes?.trim() ? touchDraft.notes.trim() : null,
                contacted_at: Number.isNaN(parsedDate.getTime()) ? new Date().toISOString() : parsedDate.toISOString(),
                updated_at: new Date().toISOString(),
                admin_id: touchDraft.admin_id,
                admin_name: touchDraft.admin_name,
            }

            let updated = null
            if (editingTouchIndex === null || editingTouchIndex === undefined) {
                updated = await updateUserApplication(answersDialog.id, { touch: payloadTouch })
            }
            else {
                const touches = (answersDialog?.touches ?? []).map((item, index) => {
                    if (index !== editingTouchIndex) {
                        return item
                    }

                    return {
                        ...item,
                        ...payloadTouch,
                        updated_at: payloadTouch.updated_at,
                    }
                })

                updated = await updateUserApplication(answersDialog.id, { touches })
            }

            if (!updated) {
                return false
            }

            setAnswersDialog((prev) => {
                if (!prev) {
                    return prev
                }

                return {
                    ...prev,
                    touches: updated?.touches ?? prev.touches ?? [],
                    updated_at: updated?.updated_at ?? prev.updated_at,
                }
            })

            setTouchDraft(initTouchDraft())
            setEditingTouchIndex(null)
            return true
        }
        finally {
            setSavingTouch(false)
        }
    }

    const openTouchCreateDialog = () => {
        setTouchDraft(initTouchDraft())
        setEditingTouchIndex(null)
        setTouchDialogOpen(true)
    }

    const openTouchEditDialog = (touch, index) => {
        const contactedAt = touch?.contacted_at ? new Date(touch.contacted_at) : new Date()
        const normalizedType = touch?.type ?? 'whatsapp'
        const normalizedOutcome = (normalizedType === 'call' && touch?.contact_outcome === 'awaiting_response')
            ? 'not_answered'
            : (touch?.contact_outcome ?? 'not_answered')
        setTouchDraft({
            type: normalizedType,
            contact_outcome: normalizedOutcome,
            lead_feedback: touch?.lead_feedback ?? '',
            notes: touch?.notes ?? '',
            contacted_at: Number.isNaN(contactedAt.getTime()) ? new Date() : contactedAt,
            admin_id: touch?.admin_id ?? context.user?.id ?? null,
            admin_name: touch?.admin_name ?? `${context.user?.name ?? ''} ${context.user?.surname ?? ''}`.trim(),
        })
        setEditingTouchIndex(index)
        setTouchDialogOpen(true)
    }

    const removeTouchFromUserApplication = async (index) => {
        if (deletingTouch || !answersDialog?.id || index === null || index === undefined) {
            return false
        }

        setDeletingTouch(true)
        try {
            const touches = (answersDialog?.touches ?? []).filter((_, touchIndex) => touchIndex !== index)
            const updated = await updateUserApplication(answersDialog.id, { touches })
            if (!updated) {
                return false
            }

            setAnswersDialog((prev) => {
                if (!prev) {
                    return prev
                }

                return {
                    ...prev,
                    touches: updated?.touches ?? touches,
                    updated_at: updated?.updated_at ?? prev.updated_at,
                }
            })

            return true
        }
        finally {
            setDeletingTouch(false)
        }
    }

    const setUserApplicationTest = async (id, test) => {
        try {
            await apiPutNoTimeout(`/admin/users_applications/${id}`, { test: test })
            let apps = usersApplications.map(app => app.id === id ? { ...app, test: test } : app)
            setUsersApplications(apps)
        }
        catch (e) {
            console.error(e)
        }
    }


    function generateBase64NoPadding(id) {
        // Crea una stringa unica combinando i dati
        const dataString = `${id + 1234}`;

        // Codifica la stringa in Base64
        let base64String = btoa(dataString);

        // Rimuovi il padding (=) dalla fine
        base64String = base64String.replace(/=+$/, '');

        // Restituisce la stringa codificata senza padding
        return base64String;
    }

    useEffect(() => {
        if (linkCopied) {
            setTimeout(() => {
                setLinkCopied(false)
            }, 1000)
        }
    }, [linkCopied])

    useEffect(() => {
        setPage(0)
        setPer(25)

    }, [filters.q, filters.courses, filters.status, filters.utmSources, filters.test, filters.startDate, filters.endDate, filters.onlyAssignedToMe, filters.assignedAdminId])

    useEffect(() => {
        try {
            localStorage.setItem(ONLY_ASSIGNED_FILTER_STORAGE_KEY, String(filters.onlyAssignedToMe))
        }
        catch (e) {
            console.error(e)
        }
    }, [filters.onlyAssignedToMe])

    useEffect(() => {
        getUsersAppications()

    }, [page, per, getUsersAppications])

    useEffect(() => {
        return () => {
            cancelUsersApplicationsRequest()
        }
    }, [cancelUsersApplicationsRequest])

    const getUserAppLogs = async (id) => {
        try {
            return await apiGetNoTimeout(`/admin/users_applications/${id}/logs`)
        }
        catch (e) {
            console.error(e)
        }
    }

    const sendEmail = async (mail_id) => {
        try {
            let mail = await apiGetNoTimeout(`/admin/mailings/${mail_id}/send`)
            setUsersApplications(usersApplications.map(ua => {
                if (ua.mails && ua.mails.length > 0 && ua.mails.find(m => m.id === mail_id)) {
                    ua.mails = ua.mails.filter(u => u.id !== mail_id)
                    ua.mails = [mail, ...ua.mails]
                }
                return ua
            }))
            setAnswersDialog((prev) => {
                prev.mails = prev.mails.filter(m => m.id !== mail_id)
                prev.mails = [mail, ...prev.mails]
                return { ...prev }
            })
        }
        catch (e) {
            console.error(e)
            setAnswersDialog((prev) => {
                prev.mails = prev.mails.map(m => {
                    if (m.id === mail_id) {
                        m.status = MailStatus.Failed
                        m.errors = [...(m.errors ?? []), { message: e.detail ?? 'Error', created_at: (new Date()).toISOString() }]
                    }
                    return m
                })
                return { ...prev }
            })
        }
    }

    const cancelMail = async (mail_id) => {
        try {
            let mail = await apiGetNoTimeout(`/admin/mailings/${mail_id}/cancel`)
            setUsersApplications(usersApplications.map(ua => {
                if (ua.mails && ua.mails.length > 0 && ua.mails.find(m => m.id === mail_id)) {
                    ua.mails = ua.mails.filter(u => u.id !== mail_id)
                    ua.mails = [mail, ...ua.mails]
                }
                return ua
            }))
            setAnswersDialog((prev) => {
                prev.mails = prev.mails.filter(m => m.id !== mail_id)
                prev.mails = [mail, ...prev.mails]
                return { ...prev }
            })
        }
        catch (e) {
            console.error(e)
        }
    }

    const rescheduleEmail = async (mail) => {
        try {
            let rescheduled = await apiGetNoTimeout(`/admin/mailings/${mail.id}/reschedule?date=${mail.reschedule_date}`)
            setUsersApplications(usersApplications.map(ua => {
                if (ua.mails && ua.mails.length > 0 && ua.mails.find(m => m.id === mail.id)) {
                    ua.mails = ua.mails.filter(u => u.id !== mail.id)
                    ua.mails = [rescheduled, ...ua.mails]
                }
                return ua
            }))
            setAnswersDialog((prev) => {
                prev.mails = prev.mails.filter(m => m.id !== mail.id)
                prev.mails = [rescheduled, ...prev.mails]
                return { ...prev }
            })
        }
        catch (e) {
            console.error(e)
        }
    }

    const getAppStatusBadge = (application) => {
        if (application.expired_at && UTCtoLocal(application.expired_at) < new Date()) {
            return { name: 'Scaduta', color: 'var(--sf-red)' }
        }
        switch (application.status) {
            case 'draft':
                return { name: 'Bozza', color: 'var(--secondary)' }
            case 'public':
                return { name: 'Pubblica', color: 'var(--sf-green)' }
            case 'hidden':
                return { name: 'Nascosta', color: 'var(--sf-red)' }
            default: return { name: application.status, color: 'var(--secondary)' }
        }
    }

    const deleteUserApplication = async (id) => {
        try {
            await apiDeleteNoTimeout(`/admin/users_applications/${id}`)
            setUsersApplications(usersApplications.filter(ua => ua.id !== id))
        } catch (e) {
            console.error(e)
        }
    }

    const getActivityStatusBadge = (activity) => {
        switch (activity.name) {
            case 'messaggio inviato':
                return { name: 'Messaggio inviato', color: 'rgba(255, 135, 48, 100%)' }
            case 'chiamata schedulata':
            case 'chiamata schedulata ':
                return { name: 'Chiamata schedulata', color: 'rgba(251, 217, 101, 100%)' }
            case 'won':
                return { name: 'Vinto', color: 'rgba(var(--sf-green-rgb), 100%)' }
            case 'lost':
                return { name: 'Perso', color: 'rgba(var(--sf-red-rgb), 100%)' }
            case 'candidatura accettata':
                return { name: 'Candidatura accettata', color: 'rgba(106, 230, 201, 100%)' }
            case 'chiamata effettuata':
                return { name: 'Chiamata effettuata', color: 'rgba(106, 230, 201, 100%)' }
            case 'candidatura rifiutata':
                return { name: 'Candidatura rifiutata', color: 'rgba(243, 55, 107, 100%)' }
            case 'prossima edizione':
                return { name: 'Prossima edizione', color: 'rgba(95, 102, 126, 50%)' }
            default:
                return { name: activity.name, color: 'rgba(95, 102, 126, 50%)' }
        }
    }

    const getLeadScoring = (app) => {
        if (app?.lead_scoring) {
            return app.lead_scoring
        }

        if (app?.lead_score_details) {
            return app.lead_score_details
        }

        if (app?.lead_score !== undefined && app?.lead_score !== null) {
            return {
                score: app.lead_score,
                priority: app.lead_priority,
                components: {
                    intent: app.lead_score_intent,
                    fit: app.lead_score_fit,
                    process: app.lead_score_process,
                    interest: app.lead_score_interest,
                },
                reasons: app.lead_score_reasons ?? [],
                metrics: app.lead_score_metrics ?? {},
                next_action: app.lead_score_next_action,
                algorithm_version: app.lead_score_version,
            }
        }

        return null
    }

    const getLeadPriorityBadge = (priority) => {
        switch (priority) {
            case 'hot':
                return { name: 'Caldo', color: 'rgba(243, 55, 107, 100%)' }
            case 'warm':
                return { name: 'Tiepido', color: 'rgba(255, 135, 48, 100%)' }
            case 'cold':
                return { name: 'Freddo', color: 'rgba(95, 102, 126, 100%)' }
            default:
                return { name: String(priority || 'N/A').toUpperCase(), color: 'rgba(var(--secondary-rgb), 100%)' }
        }
    }

    const getLeadHeatColor = (rawScore) => {
        const score = Math.max(0, Math.min(100, Number(rawScore) || 0))
        if (score >= 80) {
            return 'rgba(243, 55, 107, 100%)'
        }
        if (score >= 60) {
            return 'rgba(255, 135, 48, 100%)'
        }
        return 'rgba(91, 148, 255, 100%)'
    }

    const isLeadScorePending = (rawScore) => {
        return rawScore === null || rawScore === undefined || Number.isNaN(Number(rawScore))
    }

    const getLeadScoreDisplay = (rawScore) => {
        if (isLeadScorePending(rawScore)) {
            return 'In attesa di calcolo'
        }
        return Number(rawScore)
    }

    const getLeadScoreColor = (rawScore) => {
        if (isLeadScorePending(rawScore)) {
            return 'rgba(var(--text-color-rgb), 56%)'
        }
        return getLeadHeatColor(rawScore)
    }

    const leadReasonMeta = {
        session_pages_high: { label: 'Molte pagine', tooltip: 'Il lead ha visitato molte pagine prima della generazione candidatura.', tone: 'positive' },
        session_duration_high: { label: 'Sessione lunga', tooltip: 'La sessione prima della candidatura ha una durata elevata.', tone: 'positive' },
        high_intent_actions: { label: 'Azioni ad alta intenzione', tooltip: 'Sono stati registrati eventi forti come view_lead, checkout o add_to_cart.', tone: 'positive' },
        fast_first_funnel_event: { label: 'Primo evento funnel rapido', tooltip: 'Dalla prima visualizzazione pagina al primo evento funnel (lead o checkout) il tempo è stato molto basso: segnale positivo di intenzione immediata.', tone: 'positive' },
        fast_first_lead_event: { label: 'Primo evento lead rapido', tooltip: 'Il primo evento del funnel lead (es. view_lead o generate_lead) è avvenuto rapidamente: il lead ha mostrato interesse concreto fin da subito.', tone: 'positive' },
        fast_first_checkout_event: { label: 'Primo evento checkout rapido', tooltip: 'Il primo evento del funnel checkout (es. add_to_cart, checkout_start, checkout_fill) è avvenuto rapidamente: segnale forte di propensione all\'acquisto.', tone: 'positive' },
        slow_first_funnel_event: { label: 'Primo evento funnel lento', tooltip: 'Il primo evento funnel è arrivato con ritardo rispetto al comportamento atteso: possibile esitazione o bassa urgenza.', tone: 'negative' },
        no_funnel_event_before_lead: { label: 'Nessun evento funnel pre-lead', tooltip: 'Prima della generazione lead non sono stati registrati eventi funnel utili: tracciamento debole o percorso poco qualificato.', tone: 'negative' },
        checkout_pre_contact: { label: 'Checkout pre-contatto', tooltip: 'Il lead ha avviato il checkout prima della prima lavorazione commerciale (stato iniziale to contact/pending): segnale positivo di intento autonomo.', tone: 'positive' },
        checkout_pre_contact_fast_update: { label: 'Checkout rapido', tooltip: 'L\'ordine è stato aggiornato rapidamente dopo la creazione, indicando intenzione concreta.', tone: 'positive' },
        checkout_pre_contact_completed: { label: 'Checkout completato', tooltip: 'Il checkout è stato completato in fase pre-contatto: segnale molto forte di intenzione.', tone: 'positive' },
        checkout_started: { label: 'Checkout avviato', tooltip: 'Esiste un ordine associato o segnali di inizio checkout.', tone: 'positive' },
        checkout_after_call_or_approval: { label: 'Checkout dopo contatto qualificato', tooltip: 'Il lead ha avviato il checkout dopo una chiamata effettuata o dopo la candidatura accettata: segnale molto forte di interesse.', tone: 'positive' },
        demographic_fit_above_average: { label: 'Fit profilo alto', tooltip: 'Il profilo anagrafico converte meglio della media su questo corso.', tone: 'positive' },
        demographic_fit_below_average: { label: 'Fit profilo basso', tooltip: 'Il profilo anagrafico converte peggio della media su questo corso.', tone: 'negative' },
        utm_fit_above_average: { label: 'Fit UTM alto', tooltip: 'La combinazione source/medium/campaign converte sopra la media del corso.', tone: 'positive' },
        utm_fit_below_average: { label: 'Fit UTM basso', tooltip: 'La combinazione source/medium/campaign converte sotto la media del corso.', tone: 'negative' },
        history_based_process: { label: 'Storico processo', tooltip: 'Il punteggio process usa la cronologia reale dei cambi stato.', tone: 'positive' },
        lead_stale_in_pipeline: { label: 'Lead fermo', tooltip: 'Il lead è nello stesso stato da troppo tempo senza avanzamento.', tone: 'negative' },
        pipeline_rework: { label: 'Regressioni funnel', tooltip: 'Sono presenti passaggi indietro nel funnel commerciale.', tone: 'negative' },
        commercial_interest_active: { label: 'Interesse commerciale attivo', tooltip: 'La componente Interest è applicata perché ci sono segnali commerciali concreti: touch registrati e/o attività di checkout/ordine successiva al contatto. In questo caso lo score considera anche qualità del contatto, esito e feedback del lead.', tone: 'positive' },
    }

    const getLeadReasonBadgeColor = (tone) => {
        if (tone === 'positive') {
            return 'rgba(var(--sf-green-rgb), 100%)'
        }
        if (tone === 'negative') {
            return 'rgba(243, 55, 107, 100%)'
        }
        return 'rgba(var(--secondary-rgb), 90%)'
    }

    const getLeadReasonMeta = (reason) => {
        return leadReasonMeta[reason] ?? { label: reason, tooltip: reason }
    }

    const leadApplicationOptions = useMemo(() => {
        const courseMap = new Map((courses ?? []).map((course) => [course.id, course]))

        return (applications ?? [])
            .filter((app) => app?.id)
            .map((app) => {
                const course = courseMap.get(app.course_id)
                const courseName = course?.name ?? '---'

                return {
                    ...app,
                    id: app.id,
                    name: courseName,
                }
            })
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    }, [applications, courses])

    const openLeadDetails = (app) => {
        const baseLeadScoring = getLeadScoring(app)
        const resolvedNextAction = baseLeadScoring?.next_action ?? app?._nextAction ?? app?.lead_score_next_action ?? null
        const leadScoring = resolvedNextAction
            ? { ...(baseLeadScoring ?? {}), next_action: resolvedNextAction }
            : baseLeadScoring
        const currentApplicationId = app?.application?.id ?? app?.application_id ?? null

        setAnswersDialog({ ...app, leadScoring, logs: [], touches: app.touches ?? [] })
        setLeadApplicationSelectionId(currentApplicationId)
        setTouchDraft(initTouchDraft())
    }

    useEffect(() => {
        let active = true

        const loadLeadScoreHistory = async () => {
            const userApplicationId = answersDialog?.id

            if (!userApplicationId) {
                setLeadScoreHistory([])
                setLoadingLeadScoreHistory(false)
                return
            }

            setLoadingLeadScoreHistory(true)
            try {
                const response = await apiGetNoTimeout(`/admin/users_applications/${userApplicationId}/score_history?limit=90`)
                const rawHistory = Array.isArray(response?.history) ? response.history : []

                const normalizedHistory = rawHistory.map((entry, index) => {
                    const createdAt = entry?.created_at
                    const total = Number(entry?.score_total)

                    return {
                        score: Number.isFinite(total) ? total : null,
                        created_at: createdAt,
                        label: createdAt
                            ? `${formatDateV2(createdAt, { day: 'numeric', month: 'short' })} ${formatTimeV2(createdAt)}`
                            : `Punto ${index + 1}`,
                    }
                })

                const dedupedHistory = normalizedHistory.reduce((acc, point) => {
                    const previous = acc[acc.length - 1]
                    if (!previous || previous.score !== point.score) {
                        acc.push(point)
                    }
                    return acc
                }, [])

                const chartData = dedupedHistory.map((point, index) => ({
                    ...point,
                    index: index + 1,
                }))

                if (active) {
                    setLeadScoreHistory(chartData)
                }
            }
            catch (e) {
                console.error(e)
                if (active) {
                    setLeadScoreHistory([])
                }
            }
            finally {
                if (active) {
                    setLoadingLeadScoreHistory(false)
                }
            }
        }

        loadLeadScoreHistory()

        return () => {
            active = false
        }
    }, [answersDialog?.id])

    const renderOverviewRows = (items, emptyText, showAction = false, style) => {
        if (!loadingOverviewLeads && !items?.length) {
            return <div className={styles.leadOverviewEmpty}>{emptyText}</div>
        }

        const columns = [
            { key: 'name', label: 'NOME', type: 'custom', wrap: true },
            { key: 'createdAt', label: 'CREATO IL', type: 'custom' },
            { key: 'score', label: 'SCORE', type: 'custom' },
            { key: 'statusBadge', label: 'STATO LEAD', type: 'custom' },
            { key: 'nextActionBadge', label: 'PROSSIMA AZIONE', type: 'custom' },
        ]

        const elements = items.map((app) => {
            const actionBadge = getNextActionBadge(app._nextAction)
            const createdAtLabel = app?.created_at
                ? `${formatDateV2(app.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}, ${formatTimeV2(app.created_at)}`
                : '---'

            return {
                ...app,
                name: {
                    value: `${app?.user?.name ?? '---'} ${app?.user?.surname ?? ''}`.trim(),
                    type: 'string',
                    component: (
                        <div className={styles.leadOverviewCourseBlock}>
                            <div className={styles.leadOverviewName}>{`${app?.user?.name ?? '---'} ${app?.user?.surname ?? ''}`.trim()}</div>
                            <div className={styles.leadOverviewCourse}>{app._courseName}</div>
                        </div>
                    ),
                },
                createdAt: {
                    value: app?.created_at ?? '',
                    type: 'string',
                    component: <div className={styles.leadOverviewCreated}>{createdAtLabel}</div>,
                },
                statusBadge: {
                    value: app.status,
                    type: 'string',
                    component: <Badge size="small" {...getUserApplicationStatusBadge(app.status)} />,
                },
                nextActionBadge: {
                    value: app._nextAction,
                    type: 'string',
                    component: (
                        <div style={{ color: actionBadge.color, fontWeight: 700, fontSize: '0.72rem' }}>
                            {actionBadge.name}
                        </div>
                    ),
                },
                score: {
                    value: app._scoreSort,
                    type: 'custom',
                    component: (
                        <div style={{ color: getLeadScoreColor(app._score), fontWeight: 800 }}>
                            {getLeadScoreDisplay(app._score)}
                        </div>
                    ),
                },
            }
        })

        return (
            <Table
                style={{ overflowY: 'hidden', minHeight: '250px', borderRadius: 0, ...style }}
                loading={loadingOverviewLeads}
                loadingN={10}
                columns={columns}
                elements={elements}
                defaultSort={{ key: 'created_at', direction: 'desc' }}
                onRowClick={(app) => openLeadDetails(app)}
            />
        )
    }

    const getNextActionBadge = (action) => {
        switch (action) {
            case 'contact_now':
                return { name: 'Contatta subito', color: 'rgba(243, 55, 107, 100%)' }
            case 'contact_today':
                return { name: 'Contatta oggi', color: 'rgba(255, 135, 48, 100%)' }
            case 'follow_up_message':
                return { name: 'Invia follow-up', color: 'rgba(251, 217, 101, 100%)' }
            case 'wait_user_reply':
                return { name: 'Attendi risposta', color: 'rgba(95, 102, 126, 100%)' }
            case 'send_reminder_24h':
                return { name: 'Invia reminder 24h', color: 'rgba(255, 135, 48, 100%)' }
            case 'prepare_call':
                return { name: 'Prepara chiamata', color: 'rgba(106, 230, 201, 100%)' }
            case 'send_offer':
                return { name: 'Invia offerta', color: 'rgba(91, 148, 255, 100%)' }
            case 'verify_payment_intent':
                return { name: 'Verifica intento pagamento', color: 'rgba(91, 148, 255, 100%)' }
            case 'push_checkout':
                return { name: 'Spingi checkout', color: 'rgba(var(--sf-green-rgb), 100%)' }
            case 'requalify_lead':
                return { name: 'Riqualifica lead', color: 'rgba(251, 217, 101, 100%)' }
            case 'close_lost_confirm':
                return { name: 'Conferma chiusura perso', color: 'rgba(var(--sf-red-rgb), 100%)' }
            case 'continue_nurture':
                return { name: 'Nurturing', color: 'rgba(95, 102, 126, 100%)' }
            case 'low_priority_nurture':
                return { name: 'Nurturing soft', color: 'rgba(95, 102, 126, 100%)' }
            default:
                return { name: 'Monitoraggio', color: 'rgba(var(--secondary-rgb), 100%)' }
        }
    }

    const getNextActionTooltip = (action) => {
        switch (action) {
            case 'contact_now':
                return 'Lead molto caldo: contattalo immediatamente per non perdere il timing.'
            case 'contact_today':
                return 'Lead promettente: contatto consigliato entro oggi.'
            case 'follow_up_message':
                return 'Invia un messaggio di follow-up per riattivare l\'interesse.'
            case 'wait_user_reply':
                return 'Aspetta una risposta prima di effettuare nuovi touch sul lead.'
            case 'send_reminder_24h':
                return 'Invia un promemoria dopo 24h per riattivare il lead senza pressione eccessiva.'
            case 'prepare_call':
                return 'Preparare materiali e obiettivi prima della chiamata.'
            case 'send_offer':
                return 'Dopo il contatto, invia proposta/offerta chiara con prossimi step.'
            case 'verify_payment_intent':
                return 'Verifica se il lead ha reale intenzione di pagamento prima di forzare il checkout.'
            case 'push_checkout':
                return 'Lead vicino alla conversione: guida verso il completamento checkout.'
            case 'requalify_lead':
                return 'Aggiorna bisogni/interesse del lead con un touch di riqualifica prima dei prossimi step.'
            case 'close_lost_confirm':
                return 'Conferma che il lead e\' effettivamente perso prima di passarlo in nurturing a bassa priorita.'
            case 'continue_nurture':
                return 'Continua nurturing con contenuti e touch graduali.'
            case 'low_priority_nurture':
                return 'Lead a bassa priorita: mantenere contatto leggero nel tempo.'
            default:
                return 'Monitora il lead e rivaluta al prossimo aggiornamento dati.'
        }
    }

    const [pipeline, setPipeline] = useState(null)
    const [pipelines, setPipelines] = useState([])

    const getPipeline = async (id) => {
        try {
            let pipeline = await apiGetNoTimeout(`/admin/active-campaign/pipelines/${id}`)
            setPipeline({ ...pipeline, id: parseInt(pipeline.id), name: pipeline?.title ?? '' })
            setPipelines([{ ...pipeline, id: parseInt(pipeline.id), name: pipeline?.title ?? '' }]);
        }
        catch (e) {
            console.error(e)
        }
    }

    const searchPipelines = async (q) => {
        try {
            let pipelines = await apiGetNoTimeout(`/admin/active-campaign/pipelines?q=${q}`)
            if (pipeline) {
                let newPipelines = pipelines.filter(p => parseInt(pipeline?.id) !== p.id);
                setPipelines([pipeline, ...newPipelines.map(p => ({ ...p, id: parseInt(p.id), name: p?.title ?? '' }))]);
            } else {
                setPipelines(pipelines.map(p => ({ ...p, id: parseInt(p.id), name: p?.title ?? '' })));
            }
        }
        catch (e) {
            console.error(e)
        }
    }

    const formatSalesCurrency = (value) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
        }).format(Number(value) || 0)
    }

    const formatSalesPercent = (value) => `${Math.round((Number(value) || 0) * 10) / 10}%`

    const renderSalesComparisonCell = (currentValue, previousValue, formatter = (value) => value) => {
        const variation = calculatePercentageDifference(currentValue, previousValue)
        const isPositive = variation >= 0

        let variationLabel = '0%'
        if (variation === Infinity) {
            variationLabel = '+inf%'
        } else if (variation === -Infinity) {
            variationLabel = '-inf%'
        } else {
            variationLabel = `${isPositive ? '+' : ''}${Math.round(variation * 10) / 10}%`
        }

        return (
            <div className={styles.salesCompareCell}>
                <div className={styles.salesCompareCurrent}>
                    <span>{formatter(currentValue)}</span>
                </div>
                <div className={styles.salesCompareSubline}>
                    <span className={isPositive ? styles.salesCompareVariationPositive : styles.salesCompareVariationNegative}>
                        {variationLabel}
                    </span>
                    <span className={styles.salesComparePrevious}>({formatter(previousValue)})</span>
                </div>
            </div>
        )
    }

    const toSalesHoursLabel = (valueInHours) => {
        const hours = Number(valueInHours) || 0
        if (hours >= 24) {
            return `${Math.round((hours / 24) * 10) / 10} gg`
        }
        return `${Math.round(hours * 10) / 10} h`
    }

    const exportSalesPerformanceCsv = () => {
        const headers = [
            'Admin',
            'Lead',
            'Lead prec',
            'Touch effettuati',
            'Touch effettuati prec',
            'Touch medi per lead',
            'Touch medi per lead prec',
            'Tempo medio primo touch',
            'Tempo medio primo touch prec',
            'Chiamate con risposta',
            'Chiamate con risposta prec',
            'Chiamate non risposte',
            'Chiamate non risposte prec',
            'Tasso risposta',
            'Tasso risposta prec',
            'Lead lavorati',
            'Lead lavorati prec',
            'Messaggi',
            'Messaggi prec',
            'Chiamate',
            'Chiamate prec',
            'Conversioni',
            'Conversioni prec',
            'Conv %',
            'Conv % prec',
            'Valore generato',
            'Valore generato prec',
        ]

        const escapeCsv = (value) => {
            const stringValue = String(value ?? '')
            return `"${stringValue.replace(/"/g, '""')}"`
        }

        const rows = salesTeamPerformanceRows.map((row) => {
            const summary = row?.summary ?? {}
            const previousSummary = row?.previous_summary ?? {}

            return [
                row?.admin_name || '---',
                summary?.recent_assigned ?? 0,
                previousSummary?.recent_assigned ?? 0,
                summary?.touches_done ?? 0,
                previousSummary?.touches_done ?? 0,
                Math.round((Number(summary?.touch_per_lead ?? 0)) * 100) / 100,
                Math.round((Number(previousSummary?.touch_per_lead ?? 0)) * 100) / 100,
                Math.round((Number(summary?.avg_first_touch_hours ?? 0)) * 100) / 100,
                Math.round((Number(previousSummary?.avg_first_touch_hours ?? 0)) * 100) / 100,
                summary?.calls_answered ?? 0,
                previousSummary?.calls_answered ?? 0,
                summary?.calls_unanswered ?? 0,
                previousSummary?.calls_unanswered ?? 0,
                Math.round((Number(summary?.response_rate ?? 0)) * 100) / 100,
                Math.round((Number(previousSummary?.response_rate ?? 0)) * 100) / 100,
                summary?.lead_worked ?? 0,
                previousSummary?.lead_worked ?? 0,
                summary?.messages_sent ?? 0,
                previousSummary?.messages_sent ?? 0,
                summary?.called_leads ?? 0,
                previousSummary?.called_leads ?? 0,
                summary?.conversions ?? 0,
                previousSummary?.conversions ?? 0,
                Math.round((Number(summary?.conversion_rate ?? 0)) * 100) / 100,
                Math.round((Number(previousSummary?.conversion_rate ?? 0)) * 100) / 100,
                Number(summary?.generated_value ?? 0),
                Number(previousSummary?.generated_value ?? 0),
            ]
        })

        const csvBody = [headers, ...rows]
            .map((row) => row.map((value) => escapeCsv(value)).join(','))
            .join('\n')

        const blob = new Blob([csvBody], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `performance-vendita-${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Candidature</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={styles.leadPageHeader}>
                            <div className={typo.title}>Lead</div>
                            <Checkbox
                                size={"1.5rem"}
                                radius={"6px"}
                                value={filters.onlyAssignedToMe}
                                onChange={(checked) => {
                                    setFilters({
                                        ...filters,
                                        assignedAdminId: null,
                                        onlyAssignedToMe: checked
                                    })
                                }}
                                label="Solo assegnati a me"
                                labelPosition="end"
                            />
                        </div>
                        {canViewSalesPerformance && (
                            <Card noPadding title="PERFORMANCE DI VENDITA" style={{overflow: 'visible'}}>
                                <Button appearance="text" style={{ position: 'absolute', top: '1rem', right: '1rem' }} onClick={exportSalesPerformanceCsv}>
                                    Esporta CSV
                                </Button>
                                <div className={styles.salesPerformanceHeader}>
                                    <div className={styles.salesPerformancePickerWrap}>
                                        <EasePicker
                                            options={salesPerformanceDateOptions}
                                            placeholder="Seleziona range (default: ultimi 7 giorni)"
                                            startDate={salesPerformanceRange.startDate ? new Date(salesPerformanceRange.startDate) : undefined}
                                            endDate={salesPerformanceRange.endDate ? new Date(salesPerformanceRange.endDate) : undefined}
                                            className={styles.datePicker}
                                        />
                                    </div>
                                </div>
                                <Table
                                    style={{ borderRadius: 0 }}
                                    loading={loadingSalesTeamPerformance}
                                    loadingN={6}
                                    columns={[
                                        { key: 'adminName', label: 'ADMIN', type: 'string', wrap: true },
                                        { key: 'leads', label: 'LEAD', type: 'custom' },
                                        { key: 'touchesDone', label: 'TOUCH EFF.', type: 'custom' },
                                        { key: 'touchPerLead', label: 'TOUCH/LEAD', type: 'custom' },
                                        { key: 'avgFirstTouchHours', label: 'TEMPO 1 TOUCH', type: 'custom' },
                                        { key: 'callsAnswered', label: 'CHIAMATE RISPOSTE', type: 'custom' },
                                        { key: 'callsUnanswered', label: 'CHIAMATE NON RISPOSTE', type: 'custom' },
                                        { key: 'responseRate', label: 'TASSO RISPOSTA', type: 'custom' },
                                        { key: 'leadWorked', label: 'LEAD LAVORATI', type: 'custom' },
                                        { key: 'messagesSent', label: 'MESSAGGI', type: 'custom' },
                                        { key: 'calledLeads', label: 'CHIAMATE', type: 'custom' },
                                        { key: 'conversions', label: 'CONVERSIONI', type: 'custom' },
                                        { key: 'conversionRate', label: 'CONV %', type: 'custom' },
                                        { key: 'generatedValue', label: 'VALORE GENERATO', type: 'custom' },
                                    ]}
                                    elements={salesTeamPerformanceRows.map((row) => {
                                        const summary = row?.summary ?? {}
                                        const previousSummary = row?.previous_summary ?? {}

                                        return {
                                            id: row?.admin_id,
                                            adminName: row?.admin_name || '---',
                                            leads: {
                                                value: summary?.recent_assigned ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.recent_assigned ?? 0, previousSummary?.recent_assigned ?? 0),
                                            },
                                            touchesDone: {
                                                value: summary?.touches_done ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.touches_done ?? 0, previousSummary?.touches_done ?? 0),
                                            },
                                            touchPerLead: {
                                                value: summary?.touch_per_lead ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.touch_per_lead ?? 0, previousSummary?.touch_per_lead ?? 0, (value) => Math.round((Number(value) || 0) * 10) / 10),
                                            },
                                            avgFirstTouchHours: {
                                                value: summary?.avg_first_touch_hours ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.avg_first_touch_hours ?? 0, previousSummary?.avg_first_touch_hours ?? 0, toSalesHoursLabel),
                                            },
                                            callsAnswered: {
                                                value: summary?.calls_answered ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.calls_answered ?? 0, previousSummary?.calls_answered ?? 0),
                                            },
                                            callsUnanswered: {
                                                value: summary?.calls_unanswered ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.calls_unanswered ?? 0, previousSummary?.calls_unanswered ?? 0),
                                            },
                                            responseRate: {
                                                value: summary?.response_rate ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.response_rate ?? 0, previousSummary?.response_rate ?? 0, formatSalesPercent),
                                            },
                                            leadWorked: {
                                                value: summary?.lead_worked ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.lead_worked ?? 0, previousSummary?.lead_worked ?? 0),
                                            },
                                            messagesSent: {
                                                value: summary?.messages_sent ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.messages_sent ?? 0, previousSummary?.messages_sent ?? 0),
                                            },
                                            calledLeads: {
                                                value: summary?.called_leads ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.called_leads ?? 0, previousSummary?.called_leads ?? 0),
                                            },
                                            conversions: {
                                                value: summary?.conversions ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.conversions ?? 0, previousSummary?.conversions ?? 0),
                                            },
                                            conversionRate: {
                                                value: summary?.conversion_rate ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.conversion_rate ?? 0, previousSummary?.conversion_rate ?? 0, formatSalesPercent),
                                            },
                                            generatedValue: {
                                                value: summary?.generated_value ?? 0,
                                                type: 'number',
                                                component: renderSalesComparisonCell(summary?.generated_value ?? 0, previousSummary?.generated_value ?? 0, formatSalesCurrency),
                                            },
                                        }
                                    })}
                                />
                            </Card>
                        )}
                        <div className={styles.leadOverviewGrid}>
                            <Card noPadding title="RECENTI">
                                {renderOverviewRows(
                                    overviewLeadTables.recent,
                                    'Nessun lead recente dalla soglia prevista.',
                                    false,
                                    { maxHeight: '500px', overflowY: 'auto' }
                                )}
                            </Card>
                            <Card noPadding title="PRIORITA DI CONTATTO">
                                {renderOverviewRows(
                                    overviewLeadTables.contactPriority,
                                    'Nessun lead con priorita di contatto.',
                                    true,
                                    { maxHeight: '500px', overflowY: 'auto' }
                                )}
                            </Card>
                        </div>
                        <Card title={`LEAD • ${usersApplicationsCount}`}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                                <ManualApplicationButton onCreated={() => { getUsersAppications(); getOverviewLeads() }} />
                            </div>
                            <div className={styles.search}>
                                <TextInput type="search" placeholder={"Cerca per nome..."} onKeyUp={(v) => {
                                    setFilters({
                                        ...filters,
                                        q: v
                                    })
                                }} />
                                <FilterIcon className={`${styles.filterIcon} ${filterOpen ? styles.active : ''}`} onClick={() => {
                                    setFilterOpen(!filterOpen)
                                }} />
                            </div>
                            {courses &&
                                <div className={`${styles.filters} ${filterOpen ? styles.open : ""}`}>
                                    <div className={styles.userApplicationHeader}>
                                        <div className={styles.selectWrapper}>
                                            <EasePicker options={options} placeholder="Seleziona Date"
                                                className={styles.datePicker} />
                                        </div>
                                        <div className={styles.selectWrapper}>
                                            <Select
                                                nullable
                                                nullLabel="Tutti gli assegnatari"
                                                placeholder="Assegnatario del lead"
                                                selected={admins.find(admin => admin.id === filters.assignedAdminId) || null}
                                                options={admins}
                                                onSelect={(option) => setFilters({
                                                    ...filters,
                                                    assignedAdminId: option?.value ?? null,
                                                    onlyAssignedToMe: false,
                                                })}
                                            />
                                        </div>
                                        <div className={styles.selectWrapper}>
                                            <MultipleSelect
                                                selected={userApplicationStatus.filter(c => filters.status.includes(c.value))}
                                                options={userApplicationStatus}
                                                allLabel="Tutti gli stati"
                                                placeholder={"Seleziona Stati"}
                                                onSelect={(items) => {
                                                    setFilters({
                                                        ...filters,
                                                        status: items.map(item => item.value)
                                                    })
                                                }} />
                                        </div>
                                        <div className={styles.selectWrapper}>
                                            <MultipleSelect
                                                selected={courses.filter(c => filters.courses.includes(c.id))}
                                                options={courses.sort((a, b) => a.name.localeCompare(b.name)).filter(c => c.requires_application)}
                                                allLabel="Tutti i corsi"
                                                placeholder={"Seleziona Corsi"}
                                                onSelect={(items) => {
                                                    setFilters({
                                                        ...filters,
                                                        courses: items.map(item => item.id)
                                                    })
                                                }} />
                                        </div>
                                        <div className={styles.selectWrapper}>
                                            <MultipleSelect
                                                selected={utmSourceOptions.filter(source => filters.utmSources.includes(source.value))}
                                                options={utmSourceOptions}
                                                allLabel="Tutte le source UTM"
                                                placeholder="Seleziona source UTM"
                                                searchPlaceholder="Cerca source UTM"
                                                loading={loadingUtmSources}
                                                onSelect={(items) => {
                                                    setFilters({
                                                        ...filters,
                                                        utmSources: items.map(item => item.value)
                                                    })
                                                }} />
                                        </div>
                                        <div className={styles.selectWrapper}>
                                            <Checkbox size={"1.5rem"} radius={"6px"} value={filters.test} onChange={(checked) => {
                                                setFilters({
                                                    ...filters,
                                                    test: checked
                                                })
                                            }} label="Mostra Test" labelPosition="end"></Checkbox>
                                        </div>
                                        <Button
                                            appearance="subtle"
                                            disabled={!hasActiveSearchFilters || exportingSearch}
                                            status={exportingSearch ? ButtonStatus.Loading : undefined}
                                            onClick={exportFilteredUsersApplications}
                                        >
                                            ESPORTA RICERCA
                                        </Button>
                                    </div>
                                </div>
                            }
                            <div className={styles.tableWrapper} ref={tableRef}>
                                <Table
                                    onContextMenu={(event, element) => {
                                        if (isManagedBySalesV2(element)) {
                                            return
                                        }

                                        const actions = [
                                            {
                                                name: element.test ? 'Rimuovi test' : 'Segna come test',
                                                icon: <TestIcon />,
                                                onClick: async () => {
                                                    await setUserApplicationTest(element.id, !element.test)
                                                }
                                            },
                                            {
                                                name: 'Elimina',
                                                icon: <DeleteIcon />,
                                                accentColor: 'var(--sf-red)',
                                                onClick: async () => {
                                                    setDeleteUserApplicationId(element.id)
                                                }
                                            }
                                        ]
                                        showMenu(event, actions)

                                    }}
                                    style={{ overflowY: 'hidden', minHeight: '250px', borderRadius: 0 }}
                                    loading={loadingPage}
                                    columns={[
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: 'name', label: 'NOME', type: 'string', wrap: false },
                                        {
                                            key: 'course', label: 'CORSO', type: 'string', wrap: true, style: {
                                                height: '1.5rem', minWidth: '120px', lineHeight: '150%', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis', overflow: 'hidden', padding: '0 1rem',
                                            }
                                        },
                                        { key: 'leadScore', label: 'LEAD SCORE', type: 'custom' },
                                        { key: 'created_at', label: 'INVIATA IL', type: 'datetime' },
                                        { key: 'updated_at', label: 'MODIFICATA IL', type: 'datetime' },
                                        { key: 'adminLabel', label: 'ASSEGNATO A', type: 'string', wrap: false },
                                        { key: 'statusSelector', label: 'STATO', type: 'custom', style: { padding: 0, height: '100%' }, handleClick: true }
                                    ]}
                                    elements={usersApplications.sort((a, b) => {
                                        return new Date(b.created_at) - new Date(a.created_at)
                                    }).map(app => {
                                        const leadScoring = getLeadScoring(app)
                                        const leadScoreRaw = leadScoring?.score ?? app?.lead_score ?? null

                                        return {
                                            ...app,
                                            leadScoring,
                                            leadScore: {
                                                value: isLeadScorePending(leadScoreRaw) ? -1 : Number(leadScoreRaw),
                                                type: 'custom',
                                                component: (
                                                    <div style={{
                                                        color: getLeadScoreColor(leadScoreRaw),
                                                        fontWeight: 800,
                                                    }}>
                                                        {getLeadScoreDisplay(leadScoreRaw)}
                                                    </div>
                                                )
                                            },
                                            name: [app.user.name, app.user.surname].filter(Boolean).join(' ') + (app.test ? ' (TEST)' : '') + (isManagedBySalesV2(app) ? ' · NUOVO FUNNEL' : ''),
                                            course: app.application?.course?.name || '---',
                                            adminLabel: app.admin ? `${app.admin.name} ${app.admin.surname}` : '---',
                                            statusSelector: {
                                                value: app.status, type: 'string', component:
                                                    <Select
                                                        wrapperRef={tableRef}
                                                        disabled={isManagedBySalesV2(app)}
                                                        selected={userApplicationStatus.find(s => s.value === app.status)}
                                                        options={userApplicationStatus}
                                                        onSelect={async (value) => {
                                                            setUpdatedApplication({ application: app, status: value.value })
                                                        }}
                                                        style={getSelectStyle(app.status)}
                                                        labelStyle={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }} />
                                            }
                                        }
                                    })}
                                    defaultSort={{ key: 'created_at', direction: 'desc' }}
                                    onRowClick={async (app) => {
                                        openLeadDetails(app)
                                    }}
                                />
                            </div>
                            <Pagination total={usersApplicationsCount} per={per} page={page} group={width > 540 ? 5 : 0} onChange={p => setPage(p)} loading={loadingPage} />

                        </Card>
                    </div>
                    <div className={styles.sectionInner}>
                        <div className={typo.title}>Candidature</div>
                        <Card title={`CANDIDATURE • ${filteredApplications.length}`}>
                            <CreateButton style={{ padding: '.75rem 1rem', position: 'absolute', top: '0', right: 0 }} onClick={() => {
                                    setOpenDialog(true)
                                    initializeStatus('draft')
                                }}>
                                Aggiungi
                            </CreateButton>
                            <div className={typo.caption}>
                                REFERENCE CODE: {generateBase64NoPadding(context.user?.id)}
                            </div>
                            {/* <MultiStateSwitch states={['TUTTE', 'ATTIVE', 'SCADUTE', 'BOZZE']} selected={tab} onStateChange={onFilterStateChange} /> */}
                            <Table
                                loading={loadingApplications}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: 'name', label: 'NOME', type: 'string', wrap: true },
                                    { key: 'course', label: 'CORSO', type: 'string', wrap: true },
                                    { key: 'owner', label: 'OWNER', type: 'string', wrap: true },
                                    { key: 'expired_at', label: 'SCADENZA', type: 'datetime' },
                                    { key: 'statusBadge', label: 'STATO', type: 'custom' },
                                    { key: 'pipeline_id', label: 'PIPELINE', type: 'number' },
                                ]}
                                elements={filteredApplications.sort((a, b) => {
                                    return new Date(b.expired_at) - new Date(a.expired_at)
                                }).map(app => ({
                                    ...app,
                                    course: courses.find(c => c.id === app.course_id)?.name || '---',
                                    owner: admins.find(a => a.id === app.owner_id)?.name || '---',
                                    statusBadge: { value: app.status, type: 'string', component: <Badge size={"small"} {...getAppStatusBadge(app)} /> }
                                }))}
                                defaultSort={{ key: 'expired_at', direction: 'desc' }}
                                onRowClick={async (app) => {
                                    const rawApp = applications.find(a => a.id === app.id) || app
                                    setApplication(rawApp)
                                    if (rawApp.pipeline_id) {
                                        await getPipeline(rawApp.pipeline_id)
                                    }
                                    setOpenDialog(true)
                                }}
                            />
                        </Card>
                    </div>

                </div>
            </div>

            <Dialog
                overflow="scroll"
                open={openDialog}
                onClose={() => {
                    setApplication({ name: '', questions: [] })
                    setPipeline(null)
                    setPipelines([])
                    setOpenDialog(false)
                }}
                title="Aggiungi Candidatura"
                action={
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: application.status === 'draft' && application.id ? 'space-between' : 'flex-end', width: '100%' }}>
                        {application.status === 'draft' && application.id &&
                            <Button accentColor={'var(--sf-red)'} onClick={() => { setDeleteDialogOpen(true) }}>
                                ELIMINA
                            </Button>
                        }
                        <Button
                            style={{ padding: '.75rem 2rem' }}
                            onClick={saveApplication}
                            status={saveLoading ? ButtonStatus.Loading : null}
                            disabled={disableSave}>
                            SALVA
                        </Button>
                    </div>
                }>
                <div className={styles.applicationDialog}>
                    <div className={styles.applicationDialogRow}>
                        <div className={styles.applicationDialogItem}>
                            <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>NOME</div>
                            <TextInput type="text" placeholder="Nome" value={application?.name} onKeyUp={(value) => {
                                setApplication((prev) => {
                                    prev.name = value
                                    return { ...prev }
                                })
                            }} />
                        </div>
                        <div className={styles.applicationDialogItem}>
                            <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>STATO</div>
                            <Select selected={status.find(s => s.value === application?.status)} options={status} onSelect={(value) => {
                                setApplication((prev) => {
                                    prev.status = value.value
                                    return { ...prev }
                                })
                            }} />
                        </div>
                    </div>
                    <div className={styles.applicationDialogRow}>
                        <div className={styles.applicationDialogItem}>
                            <div className={typo.caption}>SCADENZA</div>
                            <TextInput
                                type="date"
                                value={application.expired_at ? new Date(application.expired_at + 'Z') : null}
                                placeholder="Scadenza"
                                onKeyUp={(d) => {
                                    let app = { ...application }
                                    app.expired_at = d.toISOString().split('.')[0]
                                    setApplication(app)
                                }}
                            />
                        </div>
                        <div className={styles.applicationDialogItem}>
                            <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>CORSO</div>
                            <Select nullable
                                selected={courses.filter(c => c.requires_application).find(c => c.id === application?.course_id)}
                                options={courses.filter(c => c.requires_application)} onSelect={(value) => {
                                    setApplication((prev) => {
                                        prev.course_id = value.id
                                        return { ...prev }
                                    })
                                }} />
                        </div>
                    </div>
                    {courses.find(c => c.id === application?.course_id)?.type !== CourseType.Webinar &&
                        <>
                            <div className={styles.applicationDialogRow}>
                                <div className={styles.applicationDialogItem}>
                                    <div className={typo.caption}>INIVA CONFERME MAIL</div>
                                    <Toggle checked={application.send_confirmation_email} onChange={(checked) => {
                                        setApplication((prev) => {
                                            prev.send_confirmation_email = checked
                                            return { ...prev }
                                        })
                                    }} />
                                </div>
                                <div className={styles.applicationDialogItem}>
                                    <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>TIPO DI MAIL</div>
                                    <Select
                                        selected={mailType.find(c => c.value === application?.mail_type) ?? mailType[0]}
                                        options={mailType} onSelect={(value) => {
                                            setApplication((prev) => {
                                                prev.mail_type = value.value
                                                return { ...prev }
                                            })
                                        }} />
                                </div>
                                {application.send_confirmation_email &&
                                    <div className={styles.applicationDialogItem}>
                                        <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>DELAY CONFERMA EMAIL</div>
                                        <Select
                                            selected={mailConfirmationDelay.find(c => c.value === application?.confirmation_delay)}
                                            options={mailConfirmationDelay} onSelect={(value) => {
                                                setApplication((prev) => {
                                                    prev.confirmation_delay = value.value
                                                    return { ...prev }
                                                })
                                            }} />
                                    </div>
                                }

                            </div>
                            <div className={styles.applicationDialogRow}>
                                <div className={styles.applicationDialogItem} style={{ width: '100%' }}>
                                    <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>PIPELINE</div>
                                    <Select
                                        selected={pipelines.find(p => p.id === pipeline?.id ?? 0)}
                                        options={pipelines}
                                        placeholder="Nessuna pipeline"
                                        searchPlaceholder={"Cerca pipeline..."}
                                        onSearch={searchPipelines}
                                        onSelect={(item) => {
                                            setPipeline(item)
                                            setApplication((prev) => {
                                                prev.pipeline_id = item.id
                                                return { ...prev }
                                            })
                                        }}
                                    />
                                </div>
                                <div className={styles.applicationDialogItem} style={{ width: '100%' }}>
                                    <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>OWNER</div>
                                    <Select
                                        selected={admins.find(a => a.id === application?.owner_id)}
                                        options={admins}
                                        placeholder="Nessun owner"
                                        searchPlaceholder={"Cerca owner..."}
                                        onSelect={(item) => {
                                            setApplication((prev) => {
                                                prev.owner_id = item.id
                                                return { ...prev }
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                        </>
                    }
                    <div className={styles.applicationDialogRow}>
                        <div className={styles.applicationDialogItem} style={{ width: '100%' }}>
                            <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>TITOLO</div>
                            <TextInput type="text" placeholder="Titolo" value={application?.title} onKeyUp={(value) => {
                                setApplication((prev) => {
                                    prev.title = value
                                    return { ...prev }
                                })
                            }} />
                        </div>
                    </div>
                    <div className={styles.applicationDialogRow}>
                        <div className={styles.applicationDialogItem} style={{ width: '100%' }}>
                            <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>SOTTOTITOLO</div>
                            <TextareaAutosize
                                value={application.subtitle}
                                minRows={3} maxRows={10}
                                type="text"
                                className={styles.textArea}
                                placeholder="Scrivi qui il sottotitolo..."
                                onChange={(e) => {
                                    const { value } = e.target
                                    setApplication((prev) => {
                                        prev.subtitle = value
                                        return { ...prev }
                                    })
                                }} />
                        </div>
                    </div>
                    <div className={styles.applicationDialogRow}>
                        <div className={styles.applicationDialogItem} style={{ width: '100%' }}>
                            <div className={`${typo.caption} ${styles.applicationDialogLabel}`}>DOMANDE</div>
                            <ReactSortable list={application.questions} setList={(newList) => {
                                setApplication((prev) => {
                                    prev.questions = newList
                                    return { ...prev }
                                })
                            }} >

                                <Each of={application.questions} render={(question, index) => {
                                    return (
                                        <div className={styles.question} key={index}>
                                            <DeleteIcon onClick={() => {
                                                let app = { ...application }
                                                app.questions.splice(index, 1)
                                                setApplication(app)

                                            }} className={styles.deleteQuestion} />
                                            <TextareaAutosize
                                                value={application.questions?.[index]}
                                                minRows={3} maxRows={10}
                                                type="text"
                                                className={styles.textArea}
                                                placeholder="Scrivi qui la domanda..."
                                                onChange={(e) => {
                                                    const { value } = e.target
                                                    setApplication((prev) => {
                                                        prev.questions[index] = value
                                                        return { ...prev }
                                                    })
                                                }} />
                                        </div>
                                    )
                                }} />
                            </ReactSortable>
                            <div className={styles.applicationDialogRow}>
                                <Button
                                    appearance="default"
                                    additionalClass={styles.addQuestionButton}
                                    onClick={() => {
                                        let app = { ...application }
                                        app.questions.push('')
                                        setApplication(app)
                                    }}>
                                    AGGIUNGI DOMANDA
                                    <AddIcon />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            <Dialog
                onClose={() => {
                    setAnswersDialog(null)
                    setSelectedLog(null)
                    setTouchDraft(initTouchDraft())
                    setTouchDialogOpen(false)
                }}
                style={{ maxWidth: '98vw' }}
                open={answersDialog}
                title={
                    <div className={styles.answersDialogTitle}>
                        <img className={styles.contactPicture} src={answersDialog?.user?.picture ?? UserPlaceholder} alt="user-avatar" />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {answersDialog?.user?.name} {answersDialog?.user?.surname}
                            <div style={{ fontSize: '0.75rem', color: 'rgba(var(--text-color-rgb), 0.5)' }}>
                                {answersDialog?.application?.course?.name || '---'}
                            </div>
                        </div>
                    </div>}>
                <div className={styles.answersDialog}>
                    {isManagedBySalesV2(answersDialog) &&
                        <div className={styles.salesV2ReadOnlyNotice}>
                            <div className={styles.salesV2ReadOnlyTitle}>Gestita nel nuovo funnel commerciale</div>
                            <div className={styles.salesV2ReadOnlyDescription}>
                                Da questa pagina puoi consultare i dati, ma non modificarli.
                                {answersDialog?.sales_opportunity_id ? ` Opportunity #${answersDialog.sales_opportunity_id}.` : ''}
                            </div>
                        </div>
                    }
                    <div className={styles.answersDialogRow}>
                        <div className={typo.headline}>INFORMAZIONI</div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoCard}>
                                <div className={styles.infoCardTitle}>GESTIONE LEAD</div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Stato</div>
                                    <div className={styles.infoValueControl}>
                                        <Select
                                            disabled={isManagedBySalesV2(answersDialog)}
                                            selected={userApplicationStatus.find((s) => s.value === answersDialog?.status)}
                                            options={userApplicationStatus}
                                            onSelect={async (value) => {
                                                if (!value?.value || value.value === answersDialog?.status) {
                                                    return
                                                }
                                                setUpdatedApplication({ application: answersDialog, status: value.value })
                                            }}
                                            style={{
                                                ...getSelectStyle(answersDialog?.status),
                                                width: '100%',
                                                minWidth: 0,
                                                maxWidth: '100%'
                                            }}
                                            labelStyle={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Assegnatario</div>
                                    <div className={styles.infoValueControl}>
                                        <Select
                                            disabled={isManagedBySalesV2(answersDialog)}
                                            selected={admins.find((a) => a.id === answersDialog?.admin?.id) ?? null}
                                            options={admins}
                                            onSelect={async (value) => {
                                                const nextAdminId = value?.id ?? null
                                                if (!nextAdminId || nextAdminId === answersDialog?.admin?.id) {
                                                    return
                                                }
                                                setUpdatedAssignee({
                                                    application: answersDialog,
                                                    admin_id: nextAdminId,
                                                    admin_label: value?.name ?? '',
                                                })
                                            }}
                                            style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                                            labelStyle={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Corso</div>
                                    <div className={styles.infoValueControl}>
                                        <Select
                                            disabled={isManagedBySalesV2(answersDialog)}
                                            selected={leadApplicationOptions.find((option) => option.id === (leadApplicationSelectionId ?? answersDialog?.application?.id ?? answersDialog?.application_id)) ?? null}
                                            options={leadApplicationOptions}
                                            onSelect={(value) => {
                                                if (!value?.id || !answersDialog?.id) {
                                                    return
                                                }

                                                const currentApplicationId = answersDialog?.application?.id ?? answersDialog?.application_id
                                                if (value.id === currentApplicationId) {
                                                    return
                                                }

                                                setLeadApplicationSelectionId(value.id)

                                                setUpdatedLeadApplication({
                                                    application: answersDialog,
                                                    application_id: value.id,
                                                    application_label: value.name,
                                                })
                                            }}
                                            style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                                            labelStyle={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Creato il</div>
                                    <div className={`${styles.infoValue} ${styles.infoValueText}`}>
                                        {formatDateV2(answersDialog?.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}, {formatTimeV2(answersDialog?.created_at)}
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Modificato il</div>
                                    <div className={`${styles.infoValue} ${styles.infoValueText}`}>
                                        {answersDialog?.updated_at
                                            ? `${formatDateV2(answersDialog?.updated_at, { day: 'numeric', month: 'short', year: 'numeric' })}, ${formatTimeV2(answersDialog?.updated_at)}`
                                            : '---'}
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Active Campaign</div>
                                    <div className={`${styles.infoValue} ${styles.infoValueText}`}>
                                        {answersDialog?.deal_id
                                            ? (
                                                <a
                                                    className={styles.contactValueLink}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    href={`https://startingfinance38987.activehosted.com/app/deals/${answersDialog?.deal_id}`}
                                                >
                                                    DEAL {answersDialog?.deal_id}
                                                </a>
                                            )
                                            : '---'}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.infoCard}>
                                <div className={styles.infoCardTitle}>CONTATTO</div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Email</div>
                                    <div className={`${styles.infoValue} ${styles.infoValueText}`}>
                                        {answersDialog?.user?.email
                                            ? <a className={styles.contactValueLink} href={`mailto:${answersDialog.user.email}`}>{answersDialog.user.email}</a>
                                            : '---'}
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Telefono</div>
                                    <div className={`${styles.infoValue} ${styles.infoValueText}`}>
                                        {answersDialog?.user?.phone
                                            ? <a className={styles.contactValueLink} href={getTelLink(answersDialog.user.phone)}>{answersDialog.user.phone}</a>
                                            : '---'}
                                    </div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Eta</div>
                                    <div className={`${styles.infoValue} ${styles.infoValueText}`}>{answersDialog?.user?.age || '---'}</div>
                                </div>
                                <div className={styles.infoField}>
                                    <div className={styles.infoLabel}>Contatta</div>
                                    <div className={styles.phoneInfoActions}>
                                        <button className={styles.contactActionIconButton} type="button" onClick={() => openContactQr('phone')} title="Mostra QR chiamata">
                                            <PhoneIcon className={styles.socialIcon} />
                                        </button>
                                        <button className={styles.contactActionIconButton} type="button" onClick={() => openContactQr('whatsapp')} title="Mostra QR WhatsApp">
                                            <WhatsappIcon className={styles.socialIcon} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(() => {
                        const storedUtm = getStoredLeadUtm(answersDialog?.metadata)

                        if (storedUtm.length === 0) {
                            return null
                        }

                        return (
                            <div className={styles.answersDialogRow}>
                                <div className={typo.headline}>UTM</div>
                                <div className={styles.infoGrid}>
                                    {storedUtm.map(({ key, label, value }) => (
                                        <div className={styles.infoCard} key={key}>
                                            <div className={styles.infoCardTitle}>{label.toUpperCase()}</div>
                                            <div className={`${styles.infoValue} ${styles.infoValueText}`}>{String(value)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })()}
                    {(answersDialog?.leadScoring || answersDialog?.lead_scoring || answersDialog?.lead_score_details || answersDialog?.lead_score !== undefined) &&
                        (() => {
                            const leadScoring = answersDialog?.leadScoring ?? getLeadScoring(answersDialog)
                            const interestActive = leadScoring?.metrics?.interest_active
                            const interestValue = leadScoring?.components?.interest
                            const interestDisplayValue = (interestActive && interestValue !== undefined && interestValue !== null)
                                ? interestValue
                                : 'N/A'
                            return (
                                <div className={styles.answersDialogRow}>
                                    <div className={typo.headline}>PUNTEGGIO LEAD</div>
                                    <div className={styles.leadScoreHeader}>
                                        <div className={styles.leadScorePrimary}>
                                            <div className={styles.leadScoreValue} style={{ color: getLeadScoreColor(leadScoring?.score) }}>{getLeadScoreDisplay(leadScoring?.score)}</div>
                                            <Badge size="small" {...getLeadPriorityBadge(leadScoring?.priority)} />
                                        </div>
                                        <div className={styles.leadNextAction}>
                                            <TooltipWrapper text={getNextActionTooltip(leadScoring?.next_action)}>
                                                <div>
                                                    <Badge size="small" {...getNextActionBadge(leadScoring?.next_action)} />
                                                </div>
                                            </TooltipWrapper>
                                        </div>
                                    </div>
                                    <div className={styles.leadScoreGrid}>
                                        <div className={styles.leadScoreCard}>
                                            <div className={typo.caption}>Intento</div>
                                            <div className={styles.leadScoreCardValue}>{leadScoring?.components?.intent ?? '---'}</div>
                                        </div>
                                        <div className={styles.leadScoreCard}>
                                            <div className={typo.caption}>Affinità</div>
                                            <div className={styles.leadScoreCardValue}>{leadScoring?.components?.fit ?? '---'}</div>
                                        </div>
                                        <div className={styles.leadScoreCard}>
                                            <div className={typo.caption}>Processo</div>
                                            <div className={styles.leadScoreCardValue}>{leadScoring?.components?.process ?? '---'}</div>
                                        </div>
                                        <div className={styles.leadScoreCard}>
                                            <div className={typo.caption}>Interesse</div>
                                            <div className={styles.leadScoreCardValue}>{interestDisplayValue}</div>
                                        </div>
                                    </div>
                                    {leadScoring?.reasons?.length > 0 &&
                                        <div className={styles.leadReasonsCompact} style={{ marginTop: '0.5rem' }}>
                                            <div className={styles.leadReasonsRow}>
                                                <Each of={leadScoring.reasons} render={(reason) => {
                                                    const meta = getLeadReasonMeta(reason)
                                                    return (
                                                        <div className={styles.leadReasonItem}>
                                                            <TooltipWrapper text={meta.tooltip}>
                                                                <div>
                                                                    <Badge size="small" name={meta.label} color={getLeadReasonBadgeColor(meta.tone)} />
                                                                </div>
                                                            </TooltipWrapper>
                                                        </div>
                                                    )
                                                }} />
                                            </div>
                                        </div>
                                    }
                                    {Array.isArray(leadScoring?.metrics?.first_event_reasons) && leadScoring.metrics.first_event_reasons.length > 0 &&
                                        <div className={styles.leadReasonsCompact} style={{ marginTop: '0.35rem' }}>
                                            <div className={typo.caption} style={{ marginBottom: '0.25rem' }}>First event insights</div>
                                            <div className={styles.leadReasonsRow}>
                                                <Each of={leadScoring.metrics.first_event_reasons} render={(reason) => {
                                                    const meta = getLeadReasonMeta(reason)
                                                    return (
                                                        <div className={styles.leadReasonItem}>
                                                            <TooltipWrapper text={meta.tooltip}>
                                                                <div>
                                                                    <Badge size="small" name={meta.label} color={getLeadReasonBadgeColor(meta.tone)} />
                                                                </div>
                                                            </TooltipWrapper>
                                                        </div>
                                                    )
                                                }} />
                                            </div>
                                        </div>
                                    }
                                    <div style={{ marginTop: '0.8rem' }}>
                                        <div className={typo.caption} style={{ marginBottom: '0.35rem' }}>Storico score</div>
                                        {loadingLeadScoreHistory &&
                                            <div className={typo.body}>Caricamento storico score...</div>
                                        }
                                        {!loadingLeadScoreHistory && leadScoreHistory.length === 0 &&
                                            <div className={typo.body}>Nessuno storico score disponibile.</div>
                                        }
                                        {!loadingLeadScoreHistory && leadScoreHistory.length > 0 &&
                                            <div style={{ width: '100%', height: '170px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={leadScoreHistory} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(var(--secondary-rgb), 0.22)" />
                                                        <XAxis dataKey="index" tick={{ fill: 'rgba(var(--text-color-rgb), 0.68)', fontSize: 11 }} />
                                                        <YAxis domain={[0, 100]} tick={{ fill: 'rgba(var(--text-color-rgb), 0.68)', fontSize: 11 }} />
                                                        <RechartsTooltip
                                                            formatter={(value) => [value, 'Score']}
                                                            labelFormatter={(_, payload) => payload?.[0]?.payload?.label ?? '---'}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="score"
                                                            name="Score"
                                                            stroke="rgba(91, 148, 255, 100%)"
                                                            strokeWidth={1.8}
                                                            dot={{ r: 2 }}
                                                            activeDot={{ r: 4 }}
                                                            connectNulls
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        }
                                    </div>
                                    <details>
                                        <summary className={typo.caption} style={{ cursor: 'pointer' }}>Mostra dettagli</summary>
                                        <div className={styles.leadMetricsGrid} style={{ marginTop: '0.5rem' }}>
                                            <div className={typo.body}><b>Pagine sessione:</b> {leadScoring?.metrics?.pages_in_session ?? '---'}</div>
                                            <div className={typo.body}><b>Durata sessione:</b> {leadScoring?.metrics?.session_duration_seconds ?? '---'} sec</div>
                                            <div className={typo.body}><b>Eventi chiave:</b> {leadScoring?.metrics?.key_events ?? '---'}</div>
                                            <div className={typo.body}><b>Primo evento funnel:</b> {leadScoring?.metrics?.first_funnel_event_kind ?? '---'}</div>
                                            <div className={typo.body}><b>Tempo primo evento funnel:</b> {leadScoring?.metrics?.first_funnel_event_seconds ?? '---'} sec</div>
                                            <div className={typo.body}><b>Tempo primo evento lead:</b> {leadScoring?.metrics?.time_to_first_lead_funnel_event_seconds ?? '---'} sec</div>
                                            <div className={typo.body}><b>Tempo primo evento checkout:</b> {leadScoring?.metrics?.time_to_first_checkout_funnel_event_seconds ?? '---'} sec</div>
                                            <div className={typo.body}><b>Recenza lead:</b> {leadScoring?.metrics?.lead_recency_hours ?? '---'} h</div>
                                            <div className={typo.body}><b>Checkout:</b> {leadScoring?.metrics?.checkout_order ? 'Sì' : 'No'}</div>
                                            <div className={typo.body}><b>Età stato:</b> {leadScoring?.metrics?.status_age_hours ?? '---'} h</div>
                                            <div className={typo.body}><b>Transizioni storico:</b> {leadScoring?.metrics?.history_transitions ?? '---'}</div>
                                            <div className={typo.body}><b>Regressioni storico:</b> {leadScoring?.metrics?.history_reversals ?? '---'}</div>
                                            <div className={typo.body}><b>Sorgente UTM:</b> {leadScoring?.metrics?.utm_source ?? '---'}</div>
                                            <div className={typo.body}><b>Canale UTM:</b> {leadScoring?.metrics?.utm_medium ?? '---'}</div>
                                            <div className={typo.body}><b>Campagna UTM:</b> {leadScoring?.metrics?.utm_campaign ?? '---'}</div>
                                            <div className={typo.body}><b>Strategia matching:</b> {leadScoring?.metrics?.intent_match_strategy ?? '---'}</div>
                                            <div className={typo.body}><b>Algoritmo:</b> {leadScoring?.algorithm_version ?? '---'}</div>
                                        </div>
                                    </details>
                                </div>
                            )
                        })()
                    }
                    {(answersDialog?.order || answersDialog?.order_id) &&
                        <div className={styles.answersDialogRow}>
                            <div className={typo.headline}>ORDINE</div>
                            <div className={styles.orderCard}>
                                <div className={styles.orderCardRow}>
                                    <div className={typo.body}><b>ID Ordine:</b> {answersDialog?.order?.id ?? answersDialog?.order_id}</div>
                                    <div className={typo.body}><b>Order No:</b> {answersDialog?.order?.order_no ?? '---'}</div>
                                    <div className={styles.orderCardBadge}>
                                        {answersDialog?.order?.status &&
                                            <StatusBadge {...getOrderStatusBadge(answersDialog.order.status)} />
                                        }
                                    </div>
                                    <div className={typo.body}><b>Totale:</b> {answersDialog?.order?.amount !== null && answersDialog?.order?.amount !== undefined ? `€ ${formatNumber(Number(answersDialog.order.amount) / 100)}` : '---'}</div>
                                </div>
                                <div className={styles.orderCardRow}>
                                    <div className={typo.body}><b>Creato il:</b> {answersDialog?.order?.created_at ? `${formatDateV2(answersDialog.order.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}, ${formatTimeV2(answersDialog.order.created_at)}` : '---'}</div>
                                    <div className={typo.body}><b>Aggiornato il:</b> {answersDialog?.order?.updated_at ? `${formatDateV2(answersDialog.order.updated_at, { day: 'numeric', month: 'short', year: 'numeric' })}, ${formatTimeV2(answersDialog.order.updated_at)}` : '---'}</div>
                                    {(answersDialog?.order?.id || answersDialog?.order_id) &&
                                        <a target="_blank" rel="noreferrer" href={`/orders/${answersDialog?.order?.id ?? answersDialog?.order_id}`}>APRI ORDINE</a>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                    <div className={styles.answersDialogRow}>
                        <div className={typo.headline}>TOUCH COMMERCIALI</div>
                        {(answersDialog?.touches?.length ?? 0) > 0 &&
                            <Table
                                style={{ overflowY: 'hidden', borderRadius: 0, border: 'none' }}
                                columns={[
                                    { key: 'contacted_at', label: 'DATA', type: 'custom' },
                                    { key: 'type', label: 'TIPOLOGIA', type: 'custom' },
                                    { key: 'contact_outcome', label: 'ESITO', type: 'custom' },
                                    { key: 'lead_feedback', label: 'FEEDBACK', type: 'custom' },
                                    { key: 'notes', label: 'NOTE', type: 'custom' },
                                    { key: 'updated_at', label: 'ULTIMA MODIFICA', type: 'custom' },
                                    { key: 'actions', label: 'AZIONI', type: 'custom' },
                                ]}
                                elements={(answersDialog?.touches ?? []).slice().sort((a, b) => new Date(b.contacted_at) - new Date(a.contacted_at)).map((touch) => ({
                                    ...touch,
                                    contacted_at: {
                                        value: touch.contacted_at || '',
                                        type: 'string',
                                        component: (
                                            <div className={typo.body} style={{ padding: '0.5rem' }}>
                                                {touch.contacted_at ? `${formatDateV2(touch.contacted_at, { day: 'numeric', month: 'short', year: 'numeric' }, 'it', false)}, ${formatTimeV2(touch.contacted_at, null, 'it', false)}` : '---'}
                                            </div>
                                        )
                                    },
                                    type: {
                                        value: touch.type || '',
                                        type: 'string',
                                        component: <div className={typo.body}>{getTouchTypeLabel(touch.type)}</div>,
                                    },
                                    contact_outcome: {
                                        value: touch.contact_outcome || '',
                                        type: 'string',
                                        component: <div className={typo.body}>{getTouchOutcomeLabel(touch.contact_outcome)}</div>,
                                    },
                                    lead_feedback: {
                                        value: touch.lead_feedback || '',
                                        type: 'string',
                                        component: <div className={typo.body}>{touch.lead_feedback ? getLeadFeedbackLabel(touch.lead_feedback) : '---'}</div>,
                                    },
                                    notes: {
                                        value: touch.notes || '',
                                        type: 'string',
                                        component: <div className={typo.body}>{touch.notes ?? '---'}</div>,
                                    },
                                    updated_at: {
                                        value: touch.updated_at || '',
                                        type: 'string',
                                        component: <div className={typo.body}>{touch.updated_at ? `${formatDateV2(touch.updated_at, { day: 'numeric', month: 'short', year: 'numeric' }, 'it', false)}, ${formatTimeV2(touch.updated_at, null, 'it', false)}` : '---'}</div>,
                                    },
                                    actions: {
                                        value: 'edit',
                                        type: 'string',
                                        component: (
                                            <MeatBallsMenu
                                                disabled={deletingTouch || isManagedBySalesV2(answersDialog)}
                                                actions={[
                                                    {
                                                        id: 1,
                                                        label: 'Modifica',
                                                        icon: EditIcon,
                                                        onClick: () => openTouchEditDialog(touch, (answersDialog?.touches ?? []).findIndex((item) => item === touch))
                                                    },
                                                    {
                                                        id: 2,
                                                        label: 'Rimuovi',
                                                        icon: DeleteIcon,
                                                        style: { color: 'var(--sf-red)' },
                                                        onClick: () => setDeletingTouchIndex((answersDialog?.touches ?? []).findIndex((item) => item === touch))
                                                    }
                                                ]}
                                            />
                                        ),
                                    },
                                }))}
                            />
                        }
                        {(answersDialog?.touches?.length ?? 0) === 0 &&
                            <div className={styles.noAnswers}>Nessun touch registrato.</div>
                        }
                        <div className={styles.touchActionsRow}>
                            <Button appearance="subtle" disabled={isManagedBySalesV2(answersDialog)} onClick={openTouchCreateDialog}>
                                AGGIUNGI TOUCH
                            </Button>
                        </div>
                    </div>
                    {answersDialog?.mails && answersDialog?.mails.length > 0 &&
                        <div className={styles.answersDialogRow}>
                            <div className={typo.headline}>AUTOMAZIONI MAIL</div>
                            <Each of={answersDialog.mails} render={(mail) => {
                                let mailBadge = getMailStatusBadge(mail.status)
                                let date = mail.scheduled_for
                                let error = ''
                                if ([MailStatus.Sent].includes(mail.status)) {
                                    date = mail.updated_at
                                }
                                if ([MailStatus.Failed].includes(mail.status) && mail.errors && mail.errors.length > 0) {
                                    date = mail.errors[mail.errors.length - 1].created_at
                                    error = mail.errors[mail.errors.length - 1].message
                                }
                                return (
                                    <div className={styles.mail}>
                                        <div className={styles.mailType}>
                                            {t(`mailing.type.${mail.type}`)}
                                        </div>
                                        <div className={styles.spacer}></div>
                                        <TooltipWrapper text={`${formatDateV2(date, { day: 'numeric', month: 'short', year: 'numeric' })} - ${formatTimeV2(date)} ${error ? `: ${error}` : ''}`}>
                                            <div>
                                                <StatusBadge label={mailBadge.label} color={mailBadge.color} />
                                            </div>
                                        </TooltipWrapper>

                                        <MeatBallsMenu menuIconStyle={{ width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', color: 'black' }}
                                            actions={mail.status !== MailStatus.Sent ? [
                                                {
                                                    label: "Invia ora", icon: SendIcon, style: { color: "var(--sf-primary)", fontWeight: 500 }, onClick: () => {
                                                        setSendingMail(mail)
                                                    }
                                                },
                                                {
                                                    label: "Riprogramma", icon: ClockIcon, style: { color: "var(--sf-gray)", fontWeight: 500 }, onClick: () => {
                                                        setReschedulingMail(mail)
                                                    }
                                                },
                                                {
                                                    label: "Vedi contenuto", icon: EyeIcon, style: { color: "var(--sf-green)", fontWeight: 500 }, onClick: () => {
                                                        setViewMailContent(mail)
                                                    }
                                                },
                                                {
                                                    label: "Annulla", icon: DeleteIcon, style: { color: "var(--sf-red)", fontWeight: 500 }, onClick: () => {
                                                        setCancellingMail(mail)
                                                    }
                                                }
                                            ] : [
                                                {
                                                    label: "Invia di nuovo", icon: SendIcon, style: { color: "var(--sf-primary)", fontWeight: 500 }, onClick: () => {
                                                        setSendingMail(mail)
                                                    }
                                                },
                                                {
                                                    label: "Vedi contenuto", icon: EyeIcon, style: { color: "var(--sf-green)", fontWeight: 500 }, onClick: () => {
                                                        setViewMailContent(mail)
                                                    }
                                                }
                                            ]} />

                                    </div>
                                )
                            }} />
                        </div>
                    }
                    <div className={styles.answersDialogRow}>
                        <div className={typo.headline}>LINK ACQUISTO DIRETTO</div>
                        <div className={styles.answersDialogInfo}>
                            <TooltipWrapper text="Clicca per copiare il link">
                                <div style={{ cursor: 'pointer', overflowWrap: 'anywhere' }} onClick={async () => {
                                    if (answersDialog?.application?.course?.type === CourseType.Bootcamp) {
                                        await navigator.clipboard.writeText(`${process.env.REACT_APP_WEBAPP_URL}/bootcamp/${answersDialog?.application?.course?.slug}/checkout?email=${answersDialog?.user?.email}`)
                                    }
                                    else {
                                        await navigator.clipboard.writeText(`${process.env.REACT_APP_WEBAPP_URL}/courses/${answersDialog?.application?.course?.slug}/checkout?email=${answersDialog?.user?.email}`)
                                    }
                                }}>
                                    {answersDialog?.application?.course?.type === CourseType.Bootcamp ?
                                        `${process.env.REACT_APP_WEBAPP_URL}/bootcamp/${answersDialog?.application?.course?.slug}/checkout?email=${answersDialog?.user?.email}` :
                                        `${process.env.REACT_APP_WEBAPP_URL}/courses/${answersDialog?.application?.course?.slug}/checkout?email=${answersDialog?.user?.email}`
                                    }</div>
                            </TooltipWrapper>
                        </div>
                    </div>
                    {answersDialog?.content && answersDialog?.content.length > 0 &&
                        <div className={styles.answersDialogRow}>
                            <div className={typo.headline}>RISPOSTE</div>
                            <div className={styles.answers}>
                                {!answersDialog?.content || answersDialog?.content.length === 0 ? <div className={styles.noAnswers}>Nessuna risposta</div> : null}
                                <Each of={answersDialog?.content ?? []} render={(entry, index) => {
                                    return (
                                        <div className={styles.answer}>
                                            <div className={styles.answerQuestion}>{entry.question}</div>
                                            <div className={styles.answerText}>{entry.answer}</div>
                                        </div>
                                    )
                                }} />
                            </div>
                        </div>
                    }
                    {answersDialog?.history && answersDialog?.history.length > 0 &&
                        <div className={styles.answersDialogRow}>
                            <div className={typo.headline}>CAMBI DI STATO</div>
                            <div className={styles.history}>
                                <Each of={answersDialog?.history.reverse() ?? []} render={(entry, index) => {
                                    return (
                                        <div className={styles.historyRow}>
                                            <Badge size="small" name={getActivityStatusBadge(entry).name} color={getActivityStatusBadge(entry).color} />
                                            <div className={styles.historyDate}>{formatDateV2(entry.date, { day: 'numeric', month: 'numeric', year: 'numeric' })} - {formatTimeV2(entry.date)} </div>
                                        </div>
                                    )
                                }} />
                            </div>
                        </div>
                    }
                    {/* <div className={styles.answersDialogRow}>
                        <div className={typo.headline}>EVENTI</div>
                        <div className={styles.logs}>
                            <div className={styles.logsList}>
                                <Each of={Object.keys(groupLogsByDate(answersDialog?.logs ?? []))} render={(key) => {
                                    return (
                                        <>
                                            <div className={styles.logGroupDate}>{formatDateV2(key, { day: 'numeric', month: 'short', year: 'numeric' }, 'it', true, false)}</div>
                                            <Each of={groupLogsByDate(answersDialog?.logs ?? [])[key].sort((a, b) => {
                                                return new Date(b.created_at) - new Date(a.created_at)
                                            })} render={(log) => {
                                                const { name, verb, obj, predicate, color } = getLogPhrase(log)
                                                return (
                                                    <div className={`${styles.log} ${selectedLog?.id === log.id ? styles.selected : ''}`} onClick={() => { setSelectedLog(log) }}>
                                                        <div className={styles.logPhrase}>
                                                            {name} <b style={{ color: color }}>{verb}</b> {obj} {predicate ? `(${predicate})` : ''}
                                                        </div>
                                                        <div className={styles.logTime}>
                                                            {formatTimeV2(log.created_at, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                        </div>
                                                    </div>
                                                )
                                            }} />
                                        </>
                                    )
                                }} />
                            </div>
                            {selectedLog &&
                                <div className={styles.logDetail}>
                                    <JsonView src={selectedLog} dark={true} theme="atom" enableClipboard={false} />
                                </div>
                            }
                        </div>
                    </div> */}
                </div>
            </Dialog>
            <AlertDialog open={deleteUserApplicationId} title="Eliminare la candidatura?" text="Sei sicuro di voler eliminare la candidatura? Questa operazione non può essere annullata." onClose={() => {
                setDeleteUserApplicationId(null)
            }}
                actions={[
                    {
                        label: 'ELIMINA',
                        onClick: async () => {
                            await deleteUserApplication(deleteUserApplicationId)
                            setDeleteUserApplicationId(null)
                        }
                    }
                ]}
            />
            <AlertDialog open={sendingMail} title="Inviare la mail?" text="Sei sicuro di voler inviare la mail? Questa operazione non può essere annullata." onClose={() => {
                setSendingMail(null)
            }}
                actions={[
                    {
                        label: 'INVIA',
                        onClick: async () => {
                            await sendEmail(sendingMail.id)
                            setSendingMail(null)
                        }
                    }
                ]}
            />

            <AlertDialog open={cancellingMail} title="Annullare l'invio della mail?" text="Sei sicuro di voler annullare l'invio della mail?" onClose={() => {
                setCancellingMail(null)
            }}
                actions={[
                    {
                        label: 'ANNULLA INVIO',
                        onClick: async () => {
                            await cancelMail(cancellingMail.id)
                            setCancellingMail(null)
                        }
                    }
                ]}
            />
            <Dialog open={viewMailContent} title={viewMailContent?.subject ?? 'Contenuto della mail'} onClose={() => {
                setViewMailContent(null)
            }}>
                <div className="external-html" style={{ width: '100%', height: '100%', display: 'block' }} dangerouslySetInnerHTML={{ __html: viewMailContent?.content.replace(/class="container"/g, 'class="mailContainer"') }}></div>
            </Dialog>

            <Dialog open={contactQrDialog} title={contactQrDialog?.title ?? 'QR contatto'} onClose={() => {
                setContactQrDialog(null)
            }}>
                <div className={styles.qrDialogBody}>
                    {contactQrDialog?.url &&
                        <img
                            className={styles.qrCodeImage}
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(contactQrDialog.url)}`}
                            alt="contact-qr"
                        />
                    }
                    <div className={typo.body}>Inquadra il QR da smartphone per aprire direttamente l'azione di contatto.</div>
                    {contactQrDialog?.url &&
                        <a className={styles.contactValueLink} href={contactQrDialog.url} target="_blank" rel="noreferrer">
                            {contactQrDialog?.actionLabel}
                        </a>
                    }
                </div>
            </Dialog>

            <Dialog overflow="visible" open={touchDialogOpen} title={editingTouchIndex === null ? "Aggiungi touch commerciale" : "Modifica touch commerciale"} onClose={() => {
                setTouchDialogOpen(false)
                setEditingTouchIndex(null)
            }}
                action={
                    <Button
                        disabled={savingTouch}
                        status={savingTouch ? ButtonStatus.Loading : null}
                        onClick={async () => {
                            const saved = await addTouchToUserApplication()
                            if (saved) {
                                setTouchDialogOpen(false)
                            }
                        }}>
                        {editingTouchIndex === null ? 'AGGIUNGI TOUCH' : 'SALVA MODIFICA'}
                    </Button>
                }>
                <div className={styles.touchDialogForm}>
                    <div className={styles.touchDialogFields}>
                        <div className={styles.touchDialogField}>
                            <Select
                                selected={touchTypeOptions.find((option) => option.value === touchDraft.type)}
                                options={touchTypeOptions}
                                onSelect={(option) => {
                                    setTouchDraft((prev) => ({
                                        ...prev,
                                        type: option.value,
                                        contact_outcome: (option.value === 'call' && prev.contact_outcome === 'awaiting_response') ? 'not_answered' : prev.contact_outcome,
                                    }))
                                }}
                                labelStyle={{ fontSize: '0.75rem' }}
                            />
                        </div>
                        <div className={styles.touchDialogField}>
                            <Select
                                selected={touchOutcomeOptions.find((option) => option.value === touchDraft.contact_outcome)}
                                options={getTouchDialogOutcomeOptions(touchDraft.type)}
                                onSelect={(option) => {
                                    setTouchDraft((prev) => ({
                                        ...prev,
                                        contact_outcome: option.value,
                                        lead_feedback: option.value === 'answered' ? prev.lead_feedback : '',
                                    }))
                                }}
                                labelStyle={{ fontSize: '0.75rem' }}
                            />
                        </div>
                        <div className={styles.touchDialogField}>
                            <Select
                                selected={leadFeedbackOptions.find((option) => option.value === touchDraft.lead_feedback)}
                                options={leadFeedbackOptions}
                                disabled={touchDraft.contact_outcome !== 'answered'}
                                onSelect={(option) => {
                                    setTouchDraft((prev) => ({ ...prev, lead_feedback: option.value }))
                                }}
                                labelStyle={{ fontSize: '0.75rem' }}
                            />
                        </div>
                        <div className={styles.touchDialogField}>
                            <TextInput
                                type="date"
                                value={touchDraft.contacted_at}
                                defaultValue={null}
                                placeholder="Data contatto"
                                onKeyUp={(d) => {
                                    setTouchDraft((prev) => ({
                                        ...prev,
                                        contacted_at: d instanceof Date ? d : null,
                                    }))
                                }}
                            />
                        </div>
                        <div className={styles.touchDialogField}>
                            <Select
                                selected={admins.find((admin) => admin.value === touchDraft.admin_id)}
                                options={admins}
                                onSelect={(option) => {
                                    setTouchDraft((prev) => ({
                                        ...prev,
                                        admin_id: option.value,
                                        admin_name: option.name,
                                    }))
                                }}
                                labelStyle={{ fontSize: '0.75rem' }}
                            />
                        </div>
                    </div>
                    <TextareaAutosize
                        value={touchDraft.notes}
                        minRows={2}
                        maxRows={4}
                        className={styles.textArea}
                        placeholder="Note sul contatto (opzionale)"
                        onChange={(e) => {
                            const { value } = e.target
                            setTouchDraft((prev) => ({ ...prev, notes: value }))
                        }}
                    />
                </div>
            </Dialog>

            <Dialog open={reschedulingMail} title="Riprogramma mail" onClose={() => {
                setReschedulingMail(null)
            }}
                action={
                    <Button onClick={() => {
                        rescheduleEmail(reschedulingMail)
                        setReschedulingMail(null)
                    }}>RIPROGRAMMA</Button>
                }>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minHeight: '400px', width: '100%' }}>
                    <TextInput
                        type="date"
                        value={null}
                        defaultValue={null}
                        placeholder="Riprogramma al"
                        min={new Date()}
                        onKeyUp={(d) => {
                            setReschedulingMail((prev) => {
                                prev.reschedule_date = d.toISOString().split('.')[0]
                                return { ...prev }
                            })
                        }}
                    />
                    <div>
                        Seleziona una data per riprogrammare l'invio della mail.
                    </div>
                    <div className={styles.spacer}></div>
                </div>
            </Dialog>

            <AlertDialog
                open={deleteDialogOpen}
                status={DialogStatus.Default}
                title="Eliminare la candidatura?"
                text="Sei sicuro di voler eliminare questa candidatura?"
                actions={[
                    {
                        label: 'ANNULLA',
                        onClick: () => {
                            setDeleteDialogOpen(false)
                        }
                    },
                    {
                        label: 'ELIMINA',
                        accentColor: 'var(--sf-red)',
                        onClick: async () => {
                            await deleteApplication()
                            setDeleteDialogOpen(false)
                        }
                    }
                ]}
            />
            <AlertDialog
                open={updatedApplication}
                status={DialogStatus.Default}
                onClose={() => { setUpdatedApplication(null) }}
                title={"Modificare lo stato della candidatura?"}
                text={`Sei sicuro di voler modificare lo stato della candidatura in:<br><br><b>${userApplicationStatus.find(uas => uas.value === updatedApplication?.status)?.name ?? ''}</b>?<br><br>${['approved', 'rejected'].includes(updatedApplication?.status) ? 'Il lead riceverà una notifica via email.' : ''}`}
                actions={[
                    {
                        label: 'ANNULLA',
                        onClick: () => {
                            setUpdatedApplication(null)
                        }
                    },
                    {
                        label: 'CONFERMA',
                        accentColor: 'var(--primary)',
                        onClick: async () => {
                            await updateUserApplication(updatedApplication?.application?.id, updatedApplication?.status)
                            setUpdatedApplication(null)
                        }
                    }
                ]}
            />
            <AlertDialog
                open={updatedAssignee}
                status={DialogStatus.Default}
                onClose={() => { setUpdatedAssignee(null) }}
                title={"Modificare l'assegnatario del lead?"}
                text={`Sei sicuro di voler assegnare il lead a:<br><br><b>${updatedAssignee?.admin_label ?? ''}</b>?`}
                actions={[
                    {
                        label: 'ANNULLA',
                        onClick: () => {
                            setUpdatedAssignee(null)
                        }
                    },
                    {
                        label: 'CONFERMA',
                        accentColor: 'var(--primary)',
                        onClick: async () => {
                            await updateUserApplication(updatedAssignee?.application?.id, { admin_id: updatedAssignee?.admin_id })
                            setUpdatedAssignee(null)
                        }
                    }
                ]}
            />
            <AlertDialog
                open={updatedLeadApplication}
                status={DialogStatus.Default}
                onClose={() => {
                    setLeadApplicationSelectionId(answersDialog?.application?.id ?? answersDialog?.application_id ?? null)
                    setUpdatedLeadApplication(null)
                }}
                title={"Modificare il corso del lead?"}
                text={`Sei sicuro di voler spostare il lead su:<br><br><b>${updatedLeadApplication?.application_label ?? ''}</b>?<br><br>La modifica aggiornerà anche il deal su Active Campaign.`}
                actions={[
                    {
                        label: 'ANNULLA',
                        onClick: () => {
                            setLeadApplicationSelectionId(answersDialog?.application?.id ?? answersDialog?.application_id ?? null)
                            setUpdatedLeadApplication(null)
                        }
                    },
                    {
                        label: 'CONFERMA',
                        accentColor: 'var(--primary)',
                        onClick: async () => {
                            await updateUserApplication(updatedLeadApplication?.application?.id, {
                                application_id: updatedLeadApplication?.application_id,
                            })
                            setUpdatedLeadApplication(null)
                        }
                    }
                ]}
            />
            <AlertDialog
                open={deletingTouchIndex !== null}
                status={DialogStatus.Default}
                onClose={() => {
                    if (!deletingTouch) {
                        setDeletingTouchIndex(null)
                    }
                }}
                title={"Rimuovere il touch commerciale?"}
                text={"Sei sicuro di voler eliminare questo touch? L'operazione non può essere annullata."}
                actions={[
                    {
                        label: 'ANNULLA',
                        disabled: deletingTouch,
                        onClick: () => {
                            if (!deletingTouch) {
                                setDeletingTouchIndex(null)
                            }
                        }
                    },
                    {
                        label: deletingTouch ? 'RIMOZIONE...' : 'RIMUOVI',
                        accentColor: 'var(--sf-red)',
                        disabled: deletingTouch,
                        onClick: async () => {
                            const removed = await removeTouchFromUserApplication(deletingTouchIndex)
                            if (removed) {
                                setDeletingTouchIndex(null)
                            }
                        }
                    }
                ]}
            />
        </HeaderFooterLayout >
    )

}

export default Applications
